// src/pages/ExplorePlus.tsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search,
  MapPin,
  Calendar,
  Users,
  Star,
  ArrowRight,
  CheckCircle,
  HelpCircle,
  Loader2,
  Filter,
  UserPlus,
  TrendingUp,
  ChevronDown,
  ChevronUp,
  Lock,
  Plane,
  Car,
  Compass,
  Wifi,
  Flame,
  Map as MapIcon,
  List,
  ShieldCheck,
  Sparkles,
  Globe2,
  Smartphone,
  Clock,
  CreditCard,
  ExternalLink,
  BadgeCheck,
} from "lucide-react";
import {
  GoogleMap,
  useJsApiLoader,
  Marker,
  InfoWindow,
} from "@react-google-maps/api";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { supabase } from "../services/supabaseClient";
import { trackMarketingEvent } from "../lib/marketingEvents";
import {
  GOOGLE_MAPS_API_KEY,
  GOOGLE_MAPS_AUTH_REFERRER_POLICY,
  GOOGLE_MAPS_LANGUAGE,
  GOOGLE_MAPS_LIBRARIES,
  GOOGLE_MAPS_REGION,
  GOOGLE_MAPS_SCRIPT_ID,
} from "../lib/googleMapsConfig";
import {
  FORFEO_AFFILIATE_SHMARKER,
  FORFEO_AFFILIATE_TRS,
  buildKiwiFlightWidgetUrl,
} from "../lib/kiwiAffiliate";

import { TravelerToolbox } from "../components/explorer/TravelerToolbox";
import { normalizeTravelerToolboxCategory } from "../lib/travelerAffiliateLinks";
const libraries = GOOGLE_MAPS_LIBRARIES;
const mapContainerStyle = {
  width: "100%",
  height: "100%",
  borderRadius: "2rem",
};
const centerQuebec = { lat: 46.8139, lng: -71.208 };
const customMapStyles = [
  {
    featureType: "administrative",
    elementType: "labels.text.fill",
    stylers: [{ color: "#444444" }],
  },
  {
    featureType: "landscape",
    elementType: "all",
    stylers: [{ color: "#f2f2f2" }],
  },
  { featureType: "poi", elementType: "all", stylers: [{ visibility: "off" }] },
  {
    featureType: "road",
    elementType: "all",
    stylers: [{ saturation: -100 }, { lightness: 45 }],
  },
  {
    featureType: "road.highway",
    elementType: "all",
    stylers: [{ visibility: "simplified" }],
  },
  {
    featureType: "road.arterial",
    elementType: "labels.icon",
    stylers: [{ visibility: "off" }],
  },
  {
    featureType: "transit",
    elementType: "all",
    stylers: [{ visibility: "off" }],
  },
  {
    featureType: "water",
    elementType: "all",
    stylers: [{ color: "#a5b3ce" }, { visibility: "on" }],
  },
];

type ExploreCategory =
  | "hebergements_locaux"
  | "monde"
  | "autos"
  | "experiences"
  | "esim";

type HotelItem = {
  id: string;
  roomId?: string;
  offerId?: string;
  isSecret: boolean;
  name: string;
  businessName?: string;
  roomName?: string;
  region: string;
  price: number;
  originalPrice: number;
  rating: number;
  reviewCount: number;
  image: string;
  tags: string[];
  viewers: number;
  lat: number;
  lng: number;
};

type ServiceConfig = {
  id: ExploreCategory;
  tab: string;
  eyebrow: string;
  title: string;
  highlight: string;
  subtitle: string;
  icon: React.ReactNode;
  iconBox: string;
  gradient: string;
  heroImage: string;
  partnerName: string;
  partnerLine: string;
  widgetTitle: string;
  widgetSubtitle: string;
  trust: string[];
};

type PartnerWidgetFrameProps = {
  title: string;
  scriptUrl: string;
  height?: number;
  partnerName: string;
};

type ExploreRecentSearch = {
  category: ExploreCategory;
  label: string;
  description: string;
  href: string;
  savedAt: number;
};

type ExplorePreset = {
  label: string;
  description: string;
  category: ExploreCategory;
  values: Partial<{
    destination: string;
    dates: string;
    checkOutDate: string;
    guests: number;
    flightOrigin: string;
    travelTo: string;
    pickupLocation: string;
    dropoffLocation: string;
    pickupTime: string;
    returnTime: string;
    driverAge: string;
    esimCountry: string;
    experienceType: string;
  }>;
};

type ExploreAnalyticsEvent = {
  category: ExploreCategory;
  action: string;
  label?: string;
  source?: string;
  timestamp: number;
};

type ExplorerRuntimeContext = {
  device_type: string;
  viewport: string;
  is_app_webview: boolean;
  platform: string;
  app_surface: string;
};

type ExplorerActionCard = {
  title: string;
  text: string;
  action: string;
};

type ComplianceItem = {
  title: string;
  text: string;
};

const isExploreCategory = (value: string | null): value is ExploreCategory =>
  value === "hebergements_locaux" ||
  value === "monde" ||
  value === "autos" ||
  value === "experiences" ||
  value === "esim";

const toStr = (v: unknown) => (v === null || v === undefined ? "" : String(v));
const toNum = (v: unknown, fallback = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
};

const todayIso = () => new Date().toISOString().split("T")[0];

const addDaysIso = (days: number) => {
  const date = new Date();
  date.setDate(date.getDate() + days);
  return date.toISOString().split("T")[0];
};

const serviceConfigs: Record<ExploreCategory, ServiceConfig> = {
  hebergements_locaux: {
    id: "hebergements_locaux",
    tab: "Local",
    eyebrow: "Séjours locaux certifiés",
    title: "Nos hébergements",
    highlight: "locaux",
    subtitle:
      "Trouvez des hôtels, escapades et offres mystères vérifiées par Forfeo Explore.",
    icon: <MapPin size={22} />,
    iconBox: "bg-blue-600 text-white shadow-blue-600/30",
    gradient: "from-blue-600 via-cyan-500 to-sky-400",
    heroImage:
      "https://images.unsplash.com/photo-1542314831-068cd1dbfeeb?q=80&w=2070&auto=format&fit=crop",
    partnerName: "Forfeo Explore",
    partnerLine: "",
    widgetTitle: "Recherche locale Forfeo",
    widgetSubtitle:
      "Hébergements, hôtels mystères, carte et offres prêtes à réserver.",
    trust: [
      "Certifié Forfeo",
      "Carte interactive",
      "Hôtels mystères",
      "Paiement sécurisé",
    ],
  },
  monde: {
    id: "monde",
    tab: "Vols",
    eyebrow: "Comparateur mondial",
    title: "Vols & itinéraires",
    highlight: "mondiaux",
    subtitle:
      "Préparez votre voyage depuis Forfeo Explore avec votre vrai widget affilié vols, au départ optimisé YUL.",
    icon: <Plane size={22} className="rotate-45" />,
    iconBox: "bg-blue-600 text-white shadow-blue-600/30",
    gradient: "from-blue-700 via-indigo-600 to-cyan-400",
    heroImage:
      "https://images.unsplash.com/photo-1436491865332-7a61a109cc05?q=80&w=2074&auto=format&fit=crop",
    partnerName: "Partenaire vols",
    partnerLine:
      "Comparateur de vols intégré dans une expérience Forfeo Explore claire, avec divulgation d’affiliation.",
    widgetTitle: "Comparer les vols",
    widgetSubtitle:
      "Recherche de vols en français, départ par défaut YUL et options d’hôtels lorsque le partenaire les propose.",
    trust: [
      "Partenaire affilié",
      "Départ YUL",
      "Hôtels en option",
      "Cadre sécurisé",
    ],
  },
  autos: {
    id: "autos",
    tab: "Autos",
    eyebrow: "Mobilité voyage",
    title: "Location de",
    highlight: "voitures",
    subtitle:
      "Comparez les agences de location sans quitter l’expérience Forfeo Explore.",
    icon: <Car size={22} />,
    iconBox: "bg-cyan-500 text-white shadow-cyan-500/30",
    gradient: "from-slate-950 via-cyan-700 to-blue-500",
    heroImage:
      "https://images.unsplash.com/photo-1503376780353-7e6692767b70?q=80&w=2070&auto=format&fit=crop",
    partnerName: "Partenaire auto",
    partnerLine:
      "Comparateur de location de voitures intégré dans Forfeo Explore avec une divulgation claire du rôle du partenaire.",
    widgetTitle: "Comparer les locations",
    widgetSubtitle:
      "Prise en charge, retour, assurances, agences et options de conducteur dans un cadre adapté mobile et ordinateur.",
    trust: [
      "Annulation flexible",
      "Agences mondiales",
      "Cadre sécurisé",
      "Recherche rapide",
    ],
  },
  experiences: {
    id: "experiences",
    tab: "Activités",
    eyebrow: "Expériences locales & monde",
    title: "Activités &",
    highlight: "excursions",
    subtitle:
      "Découvrez les expériences locales Forfeo et comparez des activités mondiales selon votre destination.",
    icon: <Compass size={22} />,
    iconBox: "bg-emerald-500 text-white shadow-emerald-500/30",
    gradient: "from-emerald-600 via-teal-500 to-lime-400",
    heroImage:
      "https://images.unsplash.com/photo-1500530855697-b586d89ba3ee?q=80&w=2070&auto=format&fit=crop",
    partnerName: "Partenaire activités",
    partnerLine:
      "Forfeo met les expériences locales en avant, puis complète avec un comparateur partenaire pour les activités à l’international.",
    widgetTitle: "Comparer les activités",
    widgetSubtitle:
      "Billets, excursions et idées de sorties proposés par un partenaire externe affilié à Forfeo Explore.",
    trust: ["Forfaits locaux", "Billets mondiaux", "Offrable", "Idées rapides"],
  },
  esim: {
    id: "esim",
    tab: "eSIM",
    eyebrow: "Connectivité voyage",
    title: "Restez",
    highlight: "connecté(e)",
    subtitle:
      "Trouvez une carte eSIM pour voyager sans frais d’itinérance surprise.",
    icon: <Wifi size={22} />,
    iconBox: "bg-purple-600 text-white shadow-purple-600/30",
    gradient: "from-purple-700 via-fuchsia-600 to-orange-400",
    heroImage:
      "https://images.unsplash.com/photo-1516321318423-f06f85e504b3?q=80&w=2070&auto=format&fit=crop",
    partnerName: "Partenaire eSIM",
    partnerLine:
      "Comparateur eSIM intégré dans Forfeo Explore pour aider les voyageurs à comparer les options de connectivité.",
    widgetTitle: "Choisir une eSIM",
    widgetSubtitle:
      "Données mobiles, activation rapide et forfaits par destination, sous réserve des conditions du partenaire.",
    trust: ["Activation rapide", "QR mobile", "Sans roaming", "Destination"],
  },
};

const AFFILIATE_DISCLOSURE_TITLE = "Divulgation claire";
const AFFILIATE_DISCLOSURE_TEXT =
  "Forfeo Explore peut afficher des services fournis par des partenaires externes et recevoir une commission d’affiliation lorsqu’une réservation ou un achat est effectué via ces modules. Les prix, disponibilités, conditions, annulations, taxes, frais et service après-vente sont établis par le partenaire marchand. Vérifiez toujours les conditions finales avant de réserver.";

const RECENT_SEARCH_STORAGE_KEY = "forfeo_explorer_plus_recent_searches_v1";
const EXPLORE_ANALYTICS_STORAGE_KEY = "forfeo_explorer_plus_events_v1";

const CATEGORY_LABELS: Record<ExploreCategory, string> = {
  hebergements_locaux: "Hébergements locaux",
  monde: "Vols mondiaux",
  autos: "Location de voitures",
  experiences: "Activités & excursions",
  esim: "eSIM voyage",
};

const CATEGORY_SEO: Record<ExploreCategory, { title: string; description: string }> = {
  hebergements_locaux: {
    title: "Hébergements locaux au Québec | Forfeo Explore",
    description:
      "Trouvez des hébergements locaux, hôtels mystères et séjours certifiés par Forfeo Explore au Québec.",
  },
  monde: {
    title: "Comparer des vols mondiaux | Forfeo Explore",
    description:
      "Comparez des vols avec un module partenaire affilié, départ optimisé YUL, dans une page Forfeo Explore claire et mobile.",
  },
  autos: {
    title: "Comparer les locations de voitures | Forfeo Explore",
    description:
      "Comparez les locations de voitures avec un partenaire externe affilié, dans une expérience Forfeo Explore adaptée mobile et ordinateur.",
  },
  experiences: {
    title: "Activités et excursions locales ou mondiales | Forfeo Explore",
    description:
      "Découvrez les expériences locales Forfeo et comparez des activités mondiales avec une divulgation d’affiliation claire.",
  },
  esim: {
    title: "Comparer les eSIM de voyage | Forfeo Explore",
    description:
      "Trouvez une carte eSIM pour voyager connecté avec un module partenaire intégré et une divulgation d’affiliation claire.",
  },
};

const COMPLIANCE_ITEMS: ComplianceItem[] = [
  {
    title: "Rôle de Forfeo Explore",
    text:
      "Forfeo Explore présente une expérience de découverte et peut intégrer des modules fournis par des partenaires externes. Forfeo n’est pas le transporteur aérien, l’agence de location, l’assureur, l’opérateur d’activité ni le fournisseur eSIM lorsque le module appartient à un partenaire.",
  },
  {
    title: "Prix, disponibilités et conditions",
    text:
      "Les prix, disponibilités, frais, taxes, politiques d’annulation, garanties et conditions finales sont ceux du partenaire marchand au moment de la réservation. L’utilisateur doit valider les conditions finales avant tout paiement.",
  },
  {
    title: "Affiliation et transparence",
    text:
      "Forfeo Explore peut recevoir une commission lorsqu’un achat admissible est effectué via un lien ou module partenaire, sans frais additionnels imposés par Forfeo pour cette seule redirection.",
  },
];

const getExplorerPlusDeviceContext = (): ExplorerRuntimeContext => {
  if (typeof window === "undefined") {
    return {
      device_type: "server",
      viewport: "0x0",
      is_app_webview: false,
      platform: "server",
      app_surface: "server",
    };
  }

  const ua = window.navigator.userAgent || "";
  const params = new URLSearchParams(window.location.search || "");
  const appParam = `${params.get("source") || ""} ${params.get("from") || ""} ${params.get("surface") || ""}`;
  const isIos = /iPhone|iPad|iPod/i.test(ua);
  const isAndroid = /Android/i.test(ua);
  const isNativeWebView =
    /\bwv\b|WebView|Forfeo|Expo|ReactNative|FBAN|FBAV|Instagram|Line\//i.test(ua) ||
    /mobile_app|forfeo_app|app_webview/i.test(appParam) ||
    (isIos && !/Safari/i.test(ua));
  const isStandalone =
    window.matchMedia?.("(display-mode: standalone)").matches ||
    Boolean((window.navigator as Navigator & { standalone?: boolean }).standalone);
  const isMobile = window.innerWidth <= 820 || /iPhone|iPad|Android|Mobile/i.test(ua);

  return {
    device_type: isMobile ? "mobile" : "desktop",
    viewport: `${window.innerWidth}x${window.innerHeight}`,
    is_app_webview: Boolean(isNativeWebView || isStandalone),
    platform: isIos ? "ios" : isAndroid ? "android" : isMobile ? "mobile_web" : "desktop_web",
    app_surface: isNativeWebView ? "native_webview" : isStandalone ? "standalone" : isMobile ? "mobile_browser" : "desktop_browser",
  };
};

const trackExplorePlusEvent = (event: Omit<ExploreAnalyticsEvent, "timestamp">) => {
  if (typeof window === "undefined") return;
  const payload: ExploreAnalyticsEvent = { ...event, timestamp: Date.now() };
  const device = getExplorerPlusDeviceContext();

  try {
    const raw = window.localStorage.getItem(EXPLORE_ANALYTICS_STORAGE_KEY);
    const existing = raw ? JSON.parse(raw) : [];
    const next = Array.isArray(existing) ? [payload, ...existing].slice(0, 120) : [payload];
    window.localStorage.setItem(EXPLORE_ANALYTICS_STORAGE_KEY, JSON.stringify(next));
  } catch (error) {
    console.warn("Tracking Explorer Plus local indisponible:", error);
  }

  window.dispatchEvent(new CustomEvent("forfeo:explorer-plus", { detail: payload }));

  // Lot Tracking Supabase : envoi non bloquant vers la fonction marketing existante.
  // Si la fonction ou la table n'est pas encore disponible, l'expérience utilisateur continue normalement.
  void trackMarketingEvent({
    event_type: event.action === "page_view" ? "page_view" : event.action.includes("click") ? "internal_click" : "explorer_plus_action",
    event_name: `explorer_plus_${event.action}`,
    label: event.label || CATEGORY_LABELS[event.category],
    source: event.source || "explorer_plus",
    path: `${window.location.pathname}${window.location.search}`,
    metadata: {
      module: "explorer_plus",
      category: event.category,
      category_label: CATEGORY_LABELS[event.category],
      action: event.action,
      local_timestamp: payload.timestamp,
      ...device,
    },
  });
};

const SMART_PRESETS: ExplorePreset[] = [
  {
    category: "hebergements_locaux",
    label: "Week-end à Québec",
    description: "Hébergements Forfeo, 2 voyageurs, arrivée rapide.",
    values: { destination: "Québec", dates: addDaysIso(7), checkOutDate: addDaysIso(9), guests: 2 },
  },
  {
    category: "monde",
    label: "Vol Montréal → Paris",
    description: "Recherche vol YUL vers CDG avec hôtels en option.",
    values: { flightOrigin: "YUL", travelTo: "CDG", dates: addDaysIso(30), checkOutDate: addDaysIso(38), guests: 1 },
  },
  {
    category: "autos",
    label: "Auto Montréal aéroport",
    description: "Prise en charge YUL, retour même lieu, conducteur 30-60.",
    values: { pickupLocation: "Montréal / YUL", dropoffLocation: "Montréal / YUL", dates: addDaysIso(14), checkOutDate: addDaysIso(17), pickupTime: "10:00", returnTime: "10:00", driverAge: "30-60" },
  },
  {
    category: "experiences",
    label: "Activités à Québec",
    description: "Forfaits locaux d’abord, puis billets mondiaux.",
    values: { destination: "Québec", dates: addDaysIso(10), experienceType: "Tous" },
  },
  {
    category: "esim",
    label: "eSIM France",
    description: "Données mobiles voyage pour la France.",
    values: { esimCountry: "France" },
  },
];

const normalizeAirportCode = (value: string, fallback = "YUL") => {
  const raw = toStr(value).trim();
  const upper = raw.toUpperCase();
  if (/^[A-Z]{3}$/.test(upper)) return upper;

  const normalized = raw
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

  const airports: Record<string, string> = {
    quebec: "YQB",
    "quebec city": "YQB",
    montreal: "YUL",
    montréal: "YUL",
    yul: "YUL",
    toronto: "YYZ",
    ottawa: "YOW",
    vancouver: "YVR",
    paris: "CDG",
    nice: "NCE",
    marseille: "MRS",
    miami: "MIA",
    orlando: "MCO",
    "new york": "NYC",
    londres: "LON",
    london: "LON",
    cancun: "CUN",
    cancún: "CUN",
  };

  return airports[normalized] || fallback;
};

const buildFlightScript = (origin: string, destination: string) =>
  buildKiwiFlightWidgetUrl({
    origin,
    destination,
    currency: "usd",
    locale: "fr",
    directOnly: false,
    campaignId: 111,
    promoId: 4478,
  });

const partnerScripts = {
  activities:
    `https://tpwdg.com/content?currency=USD&trs=${FORFEO_AFFILIATE_TRS}&shmarker=${FORFEO_AFFILIATE_SHMARKER}&locale=fr&city_id=107&category=3&amount=3&powered_by=true&campaign_id=137&promo_id=4497`,
  cars:
    `https://tpwdg.com/content?trs=${FORFEO_AFFILIATE_TRS}&shmarker=${FORFEO_AFFILIATE_SHMARKER}&locale=en&powered_by=true&campaign_id=172&promo_id=4850`,
};

const buildEsimScript = (_country: string) =>
  `https://tpwdg.com/content?trs=${FORFEO_AFFILIATE_TRS}&shmarker=${FORFEO_AFFILIATE_SHMARKER}&locale=en&powered_by=true&color_button=%23f2685f&color_focused=%23f2685f&secondary=%23FFFFFF&dark=%2311100f&light=%23FFFFFF&special=%23C4C4C4&border_radius=5&plain=false&no_labels=true&promo_id=8588&campaign_id=541`;

const DEMO_HOTELS: HotelItem[] = [
  {
    id: "secret_deal_1",
    isSecret: true,
    name: "Hôtel 4★ Secret",
    region: "Vieux-Québec",
    price: 135,
    originalPrice: 289,
    rating: 4.8,
    reviewCount: 193,
    image:
      "https://images.unsplash.com/photo-1551882547-ff40c63fe5fa?q=80&w=2070&auto=format&fit=crop",
    tags: ["Luxe", "Spa", "Mystère"],
    viewers: 14,
    lat: 46.8139,
    lng: -71.208,
  },
];

const FAQ_ITEMS = [
  {
    question: "C'est quoi une 'Offre Mystère' ?",
    answer:
      "Inspiré des modèles comme Hotwire, c'est une exclusivité Forfeo. Nous négocions des tarifs jusqu'à -60% avec des hôtels haut de gamme qui veulent rester discrets. Le nom de l'hôtel est révélé uniquement après la réservation.",
  },
  {
    question: "Comment fonctionne la réservation d'hôtel ?",
    answer:
      "Vous réservez directement via la plateforme Forfeo. Le paiement est sécurisé par Stripe. L'hôtelier reçoit instantanément votre demande et le paiement.",
  },
  {
    question: "Est-ce que l'inscription est payante ?",
    answer:
      "Pour les voyageurs, c'est 100% gratuit. Pour les entreprises, l'inscription de base est gratuite.",
  },
  {
    question: "Comment fonctionnent les points de fidélité ?",
    answer:
      "1$ dépensé = 1 point Forfeo. Vous pouvez utiliser ces points pour payer partiellement vos prochaines réservations.",
  },
];

const PartnerWidgetFrame: React.FC<PartnerWidgetFrameProps> = ({
  title,
  scriptUrl,
  height = 520,
  partnerName,
}) => {
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const [reloadNonce, setReloadNonce] = useState(0);
  const [ready, setReady] = useState(false);
  const [timedOut, setTimedOut] = useState(false);
  const [frameHeight, setFrameHeight] = useState(
    Math.max(440, Math.min(height + 100, 780)),
  );

  const widgetKind =
    scriptUrl.includes("promo_id=4478") ||
    scriptUrl.includes("campaign_id=111")
      ? "flights"
      : scriptUrl.includes("promo_id=4850") ||
          scriptUrl.includes("campaign_id=172")
        ? "cars"
        : scriptUrl.includes("promo_id=4497") ||
            scriptUrl.includes("campaign_id=137")
          ? "experiences"
          : "esim";

  const widgetDocument = `/affiliate-widgets/${widgetKind}.html`;
  const iframeSrc = `${widgetDocument}?reload=${reloadNonce}`;

  useEffect(() => {
    setReady(false);
    setTimedOut(false);
    setFrameHeight(Math.max(440, Math.min(height + 100, 780)));

    const timeout = window.setTimeout(() => {
      setTimedOut(true);
    }, 18000);

    return () => window.clearTimeout(timeout);
  }, [height, reloadNonce, widgetDocument]);

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (event.source !== iframeRef.current?.contentWindow) return;

      const payload = event.data;

      if (
        !payload ||
        payload.type !== "forfeo:affiliate-widget" ||
        payload.widget !== widgetKind
      ) {
        return;
      }

      const nextHeight = Number(payload.height);

      if (Number.isFinite(nextHeight)) {
        setFrameHeight(
          Math.min(Math.max(Math.round(nextHeight), 440), 1500),
        );
      }

      if (payload.ready === true) {
        setReady(true);
        setTimedOut(false);

        trackExplorePlusEvent({
          category:
            widgetKind === "flights"
              ? "monde"
              : widgetKind === "cars"
                ? "autos"
                : widgetKind === "experiences"
                  ? "experiences"
                  : "esim",
          action: "partner_widget_ready",
          label: title,
          source: "isolated-document",
        });
      }
    };

    window.addEventListener("message", onMessage);

    return () => {
      window.removeEventListener("message", onMessage);
    };
  }, [title, widgetKind]);

  const reload = () => {
    setReloadNonce((value) => value + 1);
  };

  const openStandalone = () => {
    window.open(
      `${widgetDocument}?standalone=1`,
      "_blank",
      "noopener,noreferrer",
    );
  };

  return (
    <div className="overflow-hidden rounded-[1.6rem] border border-white/70 bg-white shadow-[0_24px_70px_rgba(15,23,42,0.10)] ring-1 ring-slate-900/5 md:rounded-[2rem]">
      <div className="flex flex-col gap-3 border-b border-slate-100 bg-white px-4 py-4 md:flex-row md:items-center md:justify-between md:px-6">
        <div className="text-left">
          <p className="text-[10px] font-black uppercase tracking-[0.16em] text-slate-400">
            Service partenaire intégré
          </p>
          <h3 className="text-xl font-black leading-tight text-slate-950 md:text-2xl">
            {title}
          </h3>
        </div>

        <div
          className="inline-flex items-center gap-2 self-start rounded-full border border-emerald-100 bg-emerald-50 px-3 py-2 text-[10px] font-black uppercase tracking-[0.12em] text-emerald-700 md:self-auto md:px-4"
          aria-label={`Service partenaire : ${partnerName}`}
        >
          <ShieldCheck size={14} />
          Affiliation déclarée
        </div>
      </div>

      <div className="relative bg-slate-50 p-2 md:p-5">
        {!ready && !timedOut && (
          <div className="pointer-events-none absolute inset-2 z-10 flex items-center justify-center rounded-[1.3rem] bg-white/90 md:inset-5">
            <div className="flex items-center gap-3 rounded-full border border-slate-200 bg-white px-5 py-3 text-sm font-bold text-slate-600 shadow-sm">
              <Loader2 size={17} className="animate-spin" />
              Chargement du module partenaire…
            </div>
          </div>
        )}

        <iframe
          ref={iframeRef}
          key={iframeSrc}
          title={title}
          src={iframeSrc}
          className="relative z-0 block w-full rounded-[1.25rem] border border-slate-100 bg-white"
          style={{
            height: `${frameHeight}px`,
            minHeight: "440px",
          }}
          sandbox="allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox"
          allow="clipboard-write; fullscreen"
          referrerPolicy="strict-origin-when-cross-origin"
          loading="eager"
        />

        {timedOut && !ready && (
          <div className="absolute inset-2 z-20 flex items-center justify-center rounded-[1.3rem] border border-dashed border-slate-300 bg-white/98 p-5 text-center md:inset-5">
            <div className="max-w-md">
              <p className="text-lg font-black text-slate-950">
                Le module partenaire n’a pas pu s’afficher ici.
              </p>

              <p className="mt-2 text-sm font-semibold leading-relaxed text-slate-500">
                Une protection du navigateur ou du réseau peut bloquer le
                contenu intégré. Relancez le module ou ouvrez-le seul dans un
                nouvel onglet.
              </p>

              <div className="mt-5 flex flex-col justify-center gap-3 sm:flex-row">
                <button
                  type="button"
                  onClick={reload}
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-slate-950 px-5 py-3 text-xs font-black uppercase tracking-[0.12em] text-white"
                >
                  Réessayer
                  <Loader2 size={14} />
                </button>

                <button
                  type="button"
                  onClick={openStandalone}
                  className="inline-flex items-center justify-center rounded-full border border-slate-200 bg-white px-5 py-3 text-xs font-black uppercase tracking-[0.12em] text-slate-800"
                >
                  Ouvrir le module
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const ExplorePlus = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const widgetSectionRef = useRef<HTMLDivElement | null>(null);

  const initialCategory = isExploreCategory(searchParams.get("category"))
    ? searchParams.get("category")
    : "hebergements_locaux";

  const [category, setCategory] = useState<ExploreCategory>(
    initialCategory as ExploreCategory,
  );
  const [loading, setLoading] = useState(false);
  const [accommodations, setAccommodations] =
    useState<HotelItem[]>(DEMO_HOTELS);
  const [localExperiences, setLocalExperiences] = useState<any[]>([]);
  const [localExperiencesLoading, setLocalExperiencesLoading] = useState(false);

  const [destination, setDestination] = useState(searchParams.get("q") || "");
  const [dates, setDates] = useState(
    searchParams.get("check_in") || searchParams.get("depart") || "",
  );
  const [checkOutDate, setCheckOutDate] = useState(
    searchParams.get("check_out") || searchParams.get("return") || "",
  );
  const [guests, setGuests] = useState(
    parseInt(
      searchParams.get("guests") || searchParams.get("passengers") || "2",
      10,
    ),
  );

  const [flightOrigin, setFlightOrigin] = useState(
    searchParams.get("origin") || "YUL",
  );
  const [travelTo, setTravelTo] = useState(searchParams.get("to") || "");
  const [pickupLocation, setPickupLocation] = useState(
    searchParams.get("pickup") || "",
  );
  const [dropoffLocation, setDropoffLocation] = useState(
    searchParams.get("dropoff") || "",
  );
  const [pickupTime, setPickupTime] = useState(
    searchParams.get("pickup_time") || "10:00",
  );
  const [returnTime, setReturnTime] = useState(
    searchParams.get("return_time") || "10:00",
  );
  const [driverAge, setDriverAge] = useState(
    searchParams.get("driver_age") || "30-60",
  );
  const [esimCountry, setEsimCountry] = useState(
    searchParams.get("country") || "Canada",
  );
  const [experienceType, setExperienceType] = useState(
    searchParams.get("type") || "Tous",
  );
  const [recentSearches, setRecentSearches] = useState<ExploreRecentSearch[]>([]);
  const [showComplianceDetails, setShowComplianceDetails] = useState(false);
  const [runtimeContext, setRuntimeContext] = useState<ExplorerRuntimeContext>(() =>
    getExplorerPlusDeviceContext(),
  );

  const [viewMode, setViewMode] = useState<"grid" | "map">("grid");
  const [selectedHotel, setSelectedHotel] = useState<HotelItem | null>(null);
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  const [showFilters, setShowFilters] = useState(false);
  const [onlyMystery, setOnlyMystery] = useState(false);
  const [priceMin, setPriceMin] = useState<string>("");
  const [priceMax, setPriceMax] = useState<string>("");
  const [sortBy, setSortBy] = useState<
    "reco" | "price_asc" | "price_desc" | "rating_desc" | "discount_desc"
  >("reco");

  const { isLoaded } = useJsApiLoader({
    id: GOOGLE_MAPS_SCRIPT_ID,
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries,
    language: GOOGLE_MAPS_LANGUAGE,
    region: GOOGLE_MAPS_REGION,
    authReferrerPolicy: GOOGLE_MAPS_AUTH_REFERRER_POLICY,
  });

  const config = serviceConfigs[category];
  const isAppWebViewPro = runtimeContext.is_app_webview && runtimeContext.device_type === "mobile";

  useEffect(() => {
    if (typeof document === "undefined") return;
    document.documentElement.classList.toggle("forfeo-explorer-app-webview", isAppWebViewPro);
    document.body.classList.toggle("forfeo-explorer-app-webview", isAppWebViewPro);
    return () => {
      document.documentElement.classList.remove("forfeo-explorer-app-webview");
      document.body.classList.remove("forfeo-explorer-app-webview");
    };
  }, [isAppWebViewPro]);

  const seo = CATEGORY_SEO[category];
  const canonicalUrl = `https://forfeoexplore.com/explorer-plus?category=${category}`;
  const flightScript = useMemo(
    () => buildFlightScript(flightOrigin, travelTo),
    [flightOrigin, travelTo],
  );
  const esimScript = useMemo(() => buildEsimScript(esimCountry), [esimCountry]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const raw = window.localStorage.getItem(RECENT_SEARCH_STORAGE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        setRecentSearches(parsed.slice(0, 6));
      }
    } catch (error) {
      console.warn("Historique Explorer Plus indisponible:", error);
    }
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const updateRuntime = () => setRuntimeContext(getExplorerPlusDeviceContext());
    updateRuntime();
    window.addEventListener("resize", updateRuntime);
    window.addEventListener("orientationchange", updateRuntime);
    return () => {
      window.removeEventListener("resize", updateRuntime);
      window.removeEventListener("orientationchange", updateRuntime);
    };
  }, []);

  useEffect(() => {
    const sync = (key: string, setter: (value: string) => void) => {
      const value = searchParams.get(key);
      if (value !== null) setter(value);
    };

    sync("q", setDestination);
    sync("check_in", setDates);
    sync("depart", setDates);
    sync("pickup_date", setDates);
    sync("date", setDates);
    sync("check_out", setCheckOutDate);
    sync("return", setCheckOutDate);
    sync("return_date", setCheckOutDate);
    sync("origin", setFlightOrigin);
    sync("to", setTravelTo);
    sync("pickup", setPickupLocation);
    sync("dropoff", setDropoffLocation);
    sync("pickup_time", setPickupTime);
    sync("return_time", setReturnTime);
    sync("driver_age", setDriverAge);
    sync("country", setEsimCountry);
    sync("type", setExperienceType);

    const guestParam = searchParams.get("guests") || searchParams.get("passengers");
    if (guestParam) {
      const parsedGuests = parseInt(guestParam, 10);
      if (Number.isFinite(parsedGuests)) setGuests(parsedGuests);
    }
  }, [searchParams]);

  useEffect(() => {
    const nextCategory = searchParams.get("category");
    if (isExploreCategory(nextCategory) && nextCategory !== category) {
      setCategory(nextCategory);
    }
  }, [category, searchParams]);

  useEffect(() => {
    if (category === "hebergements_locaux") {
      fetchAccommodations();
    }
  }, [destination, dates, checkOutDate, category]);

  useEffect(() => {
    if (category === "experiences" && localExperiences.length === 0) {
      fetchLocalExperiences();
    }
  }, [category, localExperiences.length]);

  useEffect(() => {
    trackExplorePlusEvent({
      category,
      action: "page_view",
      label: CATEGORY_LABELS[category],
      source: "explorer_plus",
    });
  }, [category]);

  const fetchMysteryHotelOffers = async () => {
    const candidates = [
      "mystery_hotel_offers_public",
      "mystery_hotel_offers_public_view",
      "mystery_hotel_offers_public_v",
      "mystery_hotel_offers_public_safe",
      "mystery_hotel_offers",
    ];

    for (const name of candidates) {
      const { data, error } = await supabase.from(name).select("*").limit(50);
      if (!error && data) return data;
    }
    return [];
  };

  const fetchLocalExperiences = async () => {
    setLocalExperiencesLoading(true);
    try {
      const { data, error } = await supabase
        .from("boutique_forfaits")
        .select("*")
        .eq("is_active", true)
        .order("created_at", { ascending: false })
        .limit(8);

      if (error) throw error;
      setLocalExperiences(data || []);
    } catch (error) {
      console.warn("Forfeo Explore Plus expériences locales:", error);
      setLocalExperiences([]);
    } finally {
      setLocalExperiencesLoading(false);
    }
  };

  const fetchAccommodations = async () => {
    setLoading(true);
    try {
      const { data: businesses, error: bizError } = await supabase
        .from("businesses")
        .select("*")
        .eq("is_approved", true);
      if (bizError) throw bizError;

      const { data: rooms, error: roomError } = await supabase
        .from("accommodations")
        .select("*")
        .eq("is_available", true);
      if (roomError) throw roomError;

      let statsMap = new Map<string, any>();
      try {
        const { data: statsRows } = await supabase
          .from("business_review_stats")
          .select("business_id, review_count, rating_avg");
        if (statsRows)
          statsMap = new Map(statsRows.map((r: any) => [r.business_id, r]));
      } catch (error) {
        console.warn("Stats hébergements indisponibles:", error);
      }

      let mysteryOffers: any[] = [];
      try {
        mysteryOffers = await fetchMysteryHotelOffers();
      } catch (error) {
        console.warn("Offres mystères indisponibles:", error);
      }

      let realHotels: HotelItem[] = [];
      if (businesses && rooms && rooms.length > 0) {
        realHotels = rooms
          .map((room: any) => {
            const parentBiz = businesses.find(
              (b: any) => b.id === room.business_id,
            );
            if (!parentBiz) return null;
            const st = statsMap.get(parentBiz.id);
            const roomPrice = toNum(room.price_per_night, 0);

            return {
              id: parentBiz.id,
              roomId: room.id,
              isSecret: false,
              name: room.name,
              businessName: parentBiz.name,
              roomName: room.name,
              region: parentBiz.region || parentBiz.city || "Québec",
              price: roomPrice,
              originalPrice: Math.floor(roomPrice * 1.15),
              rating: Number.isFinite(Number(st?.rating_avg))
                ? Number(st?.rating_avg)
                : 4.9,
              reviewCount: Number.isFinite(Number(st?.review_count))
                ? Number(st?.review_count)
                : 0,
              image:
                room.images?.[0] ||
                room.image_url ||
                parentBiz.image_url ||
                "https://images.unsplash.com/photo-1566073771259-6a8506099945?q=80&w=2070&auto=format&fit=crop",
              tags: [parentBiz.category || "Hébergement", "Vérifié"],
              viewers: Math.floor(Math.random() * 12) + 2,
              lat: toNum(
                parentBiz.latitude,
                46.8139 + (Math.random() * 0.05 - 0.025),
              ),
              lng: toNum(
                parentBiz.longitude,
                -71.208 + (Math.random() * 0.05 - 0.025),
              ),
            } as HotelItem;
          })
          .filter(Boolean) as HotelItem[];
      }

      const normalizedMystery: HotelItem[] = (mysteryOffers || []).map(
        (row: any) => {
          const price = toNum(
            row.price_per_night ??
              row.discount_price ??
              row.price ??
              row.nightly_price,
            0,
          );
          return {
            id:
              row.public_id ||
              row.offer_public_id ||
              row.id ||
              `mystery_${Math.random().toString(36).slice(2)}`,
            offerId: row.public_id || row.offer_public_id || row.id,
            isSecret: true,
            name: row.title || row.name || "Hôtel Mystère",
            region:
              row.city_hint ||
              row.city ||
              row.region_hint ||
              row.region ||
              "Québec",
            price,
            originalPrice: toNum(
              row.original_price ??
                row.originalPrice ??
                (price ? Math.round(price * 1.8) : 0),
              0,
            ),
            rating: toNum(row.rating_avg ?? row.rating ?? 4.8, 4.8),
            reviewCount: toNum(row.review_count ?? row.reviewCount ?? 0, 0),
            image:
              row.image_url ||
              row.cover_image_url ||
              row.public_image_url ||
              "https://images.unsplash.com/photo-1551882547-ff40c63fe5fa?q=80&w=2070&auto=format&fit=crop",
            tags: ["Mystère", "Exclusif", "Forfeo"],
            viewers: Math.floor(Math.random() * 18) + 3,
            lat: toNum(row.latitude, 46.8139 + (Math.random() * 0.05 - 0.025)),
            lng: toNum(row.longitude, -71.208 + (Math.random() * 0.05 - 0.025)),
          };
        },
      );

      let merged = [...realHotels, ...normalizedMystery];

      if (destination) {
        const term = destination.toLowerCase();
        merged = merged.filter(
          (h: HotelItem) =>
            toStr(h.region).toLowerCase().includes(term) ||
            toStr(h.name).toLowerCase().includes(term) ||
            toStr(h.roomName).toLowerCase().includes(term),
        );
      }

      const withDemo = [...merged];
      for (const d of DEMO_HOTELS) {
        if (!withDemo.some((x: HotelItem) => x.id === d.id)) withDemo.push(d);
      }

      setAccommodations(withDemo);
    } catch (error) {
      console.warn("Chargement hébergements en mode secours:", error);
      setAccommodations(DEMO_HOTELS);
    } finally {
      setLoading(false);
    }
  };

  const buildSearchCopy = (activeCategory: ExploreCategory) => {
    if (activeCategory === "hebergements_locaux") {
      return {
        label: destination || "Hébergements locaux",
        description: `${dates || "Dates flexibles"}${checkOutDate ? ` → ${checkOutDate}` : ""} · ${guests} voyageur${guests > 1 ? "s" : ""}`,
      };
    }

    if (activeCategory === "monde") {
      return {
        label: `${flightOrigin || "YUL"} → ${travelTo || "Destination"}`,
        description: `${dates || "Départ flexible"}${checkOutDate ? ` → ${checkOutDate}` : ""} · ${guests} passager${guests > 1 ? "s" : ""}`,
      };
    }

    if (activeCategory === "autos") {
      return {
        label: pickupLocation || "Location de voiture",
        description: `${dates || "Date flexible"}${checkOutDate ? ` → ${checkOutDate}` : ""} · ${pickupTime} · ${driverAge} ans`,
      };
    }

    if (activeCategory === "experiences") {
      return {
        label: destination || "Activités & excursions",
        description: `${experienceType || "Tous"} · ${dates || "Date flexible"}`,
      };
    }

    return {
      label: esimCountry || "eSIM voyage",
      description: "Forfaits data et activation mobile selon la destination",
    };
  };

  const persistRecentSearch = (entry: ExploreRecentSearch) => {
    const next = [entry, ...recentSearches]
      .filter(
        (item, index, array) =>
          array.findIndex((x) => x.href === item.href) === index,
      )
      .slice(0, 6);

    setRecentSearches(next);
    if (typeof window !== "undefined") {
      try {
        window.localStorage.setItem(RECENT_SEARCH_STORAGE_KEY, JSON.stringify(next));
      } catch (error) {
        console.warn("Impossible de sauvegarder la recherche Explorer Plus:", error);
      }
    }
  };

  const applyPreset = (preset: ExplorePreset) => {
    if (preset.category !== category) setCategory(preset.category);
    if (preset.values.destination !== undefined) setDestination(preset.values.destination);
    if (preset.values.dates !== undefined) setDates(preset.values.dates);
    if (preset.values.checkOutDate !== undefined) setCheckOutDate(preset.values.checkOutDate);
    if (preset.values.guests !== undefined) setGuests(preset.values.guests);
    if (preset.values.flightOrigin !== undefined) setFlightOrigin(preset.values.flightOrigin);
    if (preset.values.travelTo !== undefined) setTravelTo(preset.values.travelTo);
    if (preset.values.pickupLocation !== undefined) setPickupLocation(preset.values.pickupLocation);
    if (preset.values.dropoffLocation !== undefined) setDropoffLocation(preset.values.dropoffLocation);
    if (preset.values.pickupTime !== undefined) setPickupTime(preset.values.pickupTime);
    if (preset.values.returnTime !== undefined) setReturnTime(preset.values.returnTime);
    if (preset.values.driverAge !== undefined) setDriverAge(preset.values.driverAge);
    if (preset.values.esimCountry !== undefined) setEsimCountry(preset.values.esimCountry);
    if (preset.values.experienceType !== undefined) setExperienceType(preset.values.experienceType);

    const params = new URLSearchParams();
    params.set("category", preset.category);
    if (preset.values.destination) params.set("q", preset.values.destination);
    if (preset.values.dates) {
      const dateKey = preset.category === "autos" ? "pickup_date" : preset.category === "monde" ? "depart" : preset.category === "experiences" ? "date" : "check_in";
      params.set(dateKey, preset.values.dates);
    }
    if (preset.values.checkOutDate) {
      const returnKey = preset.category === "autos" ? "return_date" : preset.category === "monde" ? "return" : "check_out";
      params.set(returnKey, preset.values.checkOutDate);
    }
    if (preset.values.guests) {
      params.set(preset.category === "monde" ? "passengers" : "guests", String(preset.values.guests));
    }
    if (preset.values.flightOrigin) params.set("origin", preset.values.flightOrigin);
    if (preset.values.travelTo) params.set("to", preset.values.travelTo);
    if (preset.values.pickupLocation) params.set("pickup", preset.values.pickupLocation);
    if (preset.values.dropoffLocation) params.set("dropoff", preset.values.dropoffLocation);
    if (preset.values.pickupTime) params.set("pickup_time", preset.values.pickupTime);
    if (preset.values.returnTime) params.set("return_time", preset.values.returnTime);
    if (preset.values.driverAge) params.set("driver_age", preset.values.driverAge);
    if (preset.values.esimCountry) params.set("country", preset.values.esimCountry);
    if (preset.values.experienceType) params.set("type", preset.values.experienceType);

    trackExplorePlusEvent({
      category: preset.category,
      action: "preset_apply",
      label: preset.label,
      source: "smart_search",
    });
    navigate(`/explorer-plus?${params.toString()}`);
    if (preset.category !== "hebergements_locaux") {
      window.setTimeout(
        () => widgetSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }),
        140,
      );
    }
  };

  const handleSearch = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    const params = new URLSearchParams();
    params.set("category", category);

    if (category === "hebergements_locaux") {
      if (destination) params.set("q", destination);
      if (dates) params.set("check_in", dates);
      if (checkOutDate) params.set("check_out", checkOutDate);
      if (guests) params.set("guests", String(guests));
    }

    if (category === "monde") {
      if (flightOrigin) params.set("origin", flightOrigin);
      if (travelTo) params.set("to", travelTo);
      if (dates) params.set("depart", dates);
      if (checkOutDate) params.set("return", checkOutDate);
      if (guests) params.set("passengers", String(guests));
    }

    if (category === "autos") {
      if (pickupLocation) params.set("pickup", pickupLocation);
      if (dropoffLocation) params.set("dropoff", dropoffLocation);
      if (dates) params.set("pickup_date", dates);
      if (checkOutDate) params.set("return_date", checkOutDate);
      if (pickupTime) params.set("pickup_time", pickupTime);
      if (returnTime) params.set("return_time", returnTime);
      if (driverAge) params.set("driver_age", driverAge);
    }

    if (category === "experiences") {
      if (destination) params.set("q", destination);
      if (dates) params.set("date", dates);
      if (experienceType) params.set("type", experienceType);
    }

    if (category === "esim") {
      if (esimCountry) params.set("country", esimCountry);
    }

    const href = `/explorer-plus?${params.toString()}`;
    const copy = buildSearchCopy(category);
    persistRecentSearch({
      category,
      label: copy.label,
      description: copy.description,
      href,
      savedAt: Date.now(),
    });

    trackExplorePlusEvent({
      category,
      action: "search_submit",
      label: copy.label,
      source: "search_panel",
    });

    navigate(href);
    if (category === "hebergements_locaux") fetchAccommodations();
    if (category !== "hebergements_locaux") {
      window.setTimeout(
        () =>
          widgetSectionRef.current?.scrollIntoView({
            behavior: "smooth",
            block: "start",
          }),
        100,
      );
    }
  };

  const filteredAccommodations = useMemo(() => {
    let list = [...accommodations];
    if (onlyMystery) list = list.filter((x: HotelItem) => x.isSecret);
    const min = priceMin !== "" ? toNum(priceMin, 0) : null;
    const max = priceMax !== "" ? toNum(priceMax, 0) : null;
    if (min !== null)
      list = list.filter((x: HotelItem) => toNum(x.price, 0) >= min);
    if (max !== null)
      list = list.filter((x: HotelItem) => toNum(x.price, 0) <= max);

    if (sortBy === "price_asc")
      list.sort(
        (a: HotelItem, b: HotelItem) => toNum(a.price, 0) - toNum(b.price, 0),
      );
    if (sortBy === "price_desc")
      list.sort(
        (a: HotelItem, b: HotelItem) => toNum(b.price, 0) - toNum(a.price, 0),
      );
    if (sortBy === "rating_desc")
      list.sort(
        (a: HotelItem, b: HotelItem) => toNum(b.rating, 0) - toNum(a.rating, 0),
      );
    if (sortBy === "discount_desc") {
      list.sort((a: HotelItem, b: HotelItem) => {
        const da = toNum(a.originalPrice, 0)
          ? 1 - toNum(a.price, 0) / toNum(a.originalPrice, 1)
          : 0;
        const db = toNum(b.originalPrice, 0)
          ? 1 - toNum(b.price, 0) / toNum(b.originalPrice, 1)
          : 0;
        return db - da;
      });
    }
    return list;
  }, [accommodations, onlyMystery, priceMin, priceMax, sortBy]);

  const filteredLocalExperiences = useMemo(() => {
    const term = destination.toLowerCase().trim();
    return localExperiences.filter((item: any) => {
      const matchesTerm =
        !term ||
        toStr(item.title).toLowerCase().includes(term) ||
        toStr(item.business_name).toLowerCase().includes(term) ||
        toStr(item.location).toLowerCase().includes(term) ||
        toStr(item.category).toLowerCase().includes(term);
      const matchesType =
        experienceType === "Tous" ||
        toStr(item.category).toLowerCase() === experienceType.toLowerCase();
      return matchesTerm && matchesType;
    });
  }, [destination, experienceType, localExperiences]);

  const switchTab = (newCategory: ExploreCategory) => {
    setCategory(newCategory);
    const params = new URLSearchParams(searchParams);
    params.set("category", newCategory);

    const sharedDestination =
      destination || travelTo || pickupLocation || esimCountry || searchParams.get("q") || "";
    if (newCategory === "hebergements_locaux" && sharedDestination) params.set("q", sharedDestination);
    if (newCategory === "monde") {
      if (flightOrigin) params.set("origin", flightOrigin);
      if (sharedDestination && !params.get("to")) params.set("to", sharedDestination);
      if (dates) params.set("depart", dates);
      if (checkOutDate) params.set("return", checkOutDate);
      if (guests) params.set("passengers", String(guests));
    }
    if (newCategory === "autos") {
      if (sharedDestination && !params.get("pickup")) params.set("pickup", sharedDestination);
      if (dates) params.set("pickup_date", dates);
      if (checkOutDate) params.set("return_date", checkOutDate);
    }
    if (newCategory === "experiences" && sharedDestination) params.set("q", sharedDestination);
    if (newCategory === "esim" && sharedDestination) params.set("country", sharedDestination);

    trackExplorePlusEvent({
      category: newCategory,
      action: "service_switch",
      label: CATEGORY_LABELS[newCategory],
      source: "service_tabs",
    });
    navigate(`/explorer-plus?${params.toString()}`);
  };

  const navigateToHotel = (item: HotelItem) => {
    const params = new URLSearchParams();
    if (dates) params.set("check_in", dates);
    if (checkOutDate) params.set("check_out", checkOutDate);
    if (item.roomId) params.set("legacyRoomId", item.roomId);
    params.set("partySize", String(Math.max(1, guests)));
    const query = params.toString();
    navigate(
      item.isSecret
        ? `/last-call?mystery=1&offer=${encodeURIComponent(toStr(item.offerId || item.id))}`
        : `/place/${item.id}${query ? `?${query}` : ""}`,
    );
  };

  const renderSmartSearchLayer = () => {
    const activePresets = SMART_PRESETS.filter(
      (preset) => preset.category === category || category === "hebergements_locaux",
    ).slice(0, 5);

    const copy = buildSearchCopy(category);
    const searchQuality =
      category === "monde"
        ? travelTo
          ? "Prêt pour le comparateur vols"
          : "Ajoutez une destination pour préciser le widget"
        : category === "autos"
          ? pickupLocation
            ? "Lieu de prise en charge détecté"
            : "Ajoutez un lieu de prise en charge"
          : category === "experiences"
            ? destination || experienceType !== "Tous"
              ? "Forfaits locaux filtrés avant le module mondial"
              : "Recherche large sur toutes les activités"
            : category === "esim"
              ? `Pays synchronisé : ${esimCountry || "Canada"}`
              : `${filteredAccommodations.length} résultat${filteredAccommodations.length > 1 ? "s" : ""} local${filteredAccommodations.length > 1 ? "aux" : ""}`;

    return (
      <div className="mx-auto mt-5 grid max-w-6xl gap-3 text-left md:grid-cols-[1.15fr_0.85fr]">
        <div className="rounded-[1.6rem] border border-white/70 bg-white p-4 shadow-xl backdrop-blur-xl md:rounded-[2rem] md:border-white/40 md:bg-white/92 md:p-5">
          <div className="mb-3 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div>
              <p className="text-[9px] font-black uppercase tracking-[0.18em] text-slate-400">
                Recherche intelligente
              </p>
              <h3 className="text-lg font-black leading-tight text-slate-950 md:text-xl">
                {copy.label}
              </h3>
              <p className="mt-1 text-xs font-semibold text-slate-500">
                {copy.description}
              </p>
            </div>
            <span className="inline-flex items-center justify-center gap-2 rounded-full bg-slate-950 px-3 py-2 text-[9px] font-black uppercase tracking-[0.13em] text-white">
              <Sparkles size={12} /> {CATEGORY_LABELS[category]}
            </span>
          </div>
          <div className="grid gap-2 md:grid-cols-2">
            <div className="rounded-2xl bg-white p-3 shadow-sm ring-1 ring-slate-100">
              <p className="text-[9px] font-black uppercase tracking-[0.15em] text-slate-400">
                Résumé
              </p>
              <p className="mt-1 text-sm font-black text-slate-950">
                {searchQuality}
              </p>
            </div>
            <div className="rounded-2xl bg-white p-3 shadow-sm ring-1 ring-slate-100">
              <p className="text-[9px] font-black uppercase tracking-[0.15em] text-slate-400">
                Prochaine étape
              </p>
              <button
                type="button"
                onClick={() => handleSearch()}
                className="mt-1 inline-flex items-center gap-2 text-sm font-black text-blue-700"
              >
                Actualiser les résultats <ArrowRight size={14} />
              </button>
            </div>
          </div>
        </div>

        <div className="rounded-[1.6rem] border border-white/70 bg-white p-4 shadow-xl backdrop-blur-xl md:rounded-[2rem] md:border-white/40 md:bg-white/92 md:p-5">
          <div className="mb-3 flex items-center justify-between gap-3">
            <div>
              <p className="text-[9px] font-black uppercase tracking-[0.18em] text-slate-400">
                Raccourcis
              </p>
              <h3 className="text-lg font-black text-slate-950">
                Recherches rapides
              </h3>
            </div>
            <TrendingUp size={18} className="text-blue-600" />
          </div>
          <div className="flex flex-wrap gap-2">
            {activePresets.map((preset) => (
              <button
                key={`${preset.category}-${preset.label}`}
                type="button"
                onClick={() => applyPreset(preset)}
                className="rounded-full border border-slate-200 bg-slate-50 px-3 py-2 text-[10px] font-black uppercase tracking-[0.12em] text-slate-700 transition hover:border-blue-200 hover:bg-blue-50 hover:text-blue-700"
              >
                {preset.label}
              </button>
            ))}
          </div>
          {recentSearches.length > 0 && (
            <div className="mt-4 border-t border-slate-100 pt-4">
              <p className="mb-2 text-[9px] font-black uppercase tracking-[0.18em] text-slate-400">
                Récentes
              </p>
              <div className="grid gap-2">
                {recentSearches.slice(0, 2).map((item) => (
                  <button
                    key={`${item.href}-${item.savedAt}`}
                    type="button"
                    onClick={() => navigate(item.href)}
                    className="flex items-center justify-between gap-3 rounded-2xl bg-slate-50 px-3 py-2 text-left transition hover:bg-blue-50"
                  >
                    <span className="min-w-0">
                      <span className="block truncate text-xs font-black text-slate-950">
                        {item.label}
                      </span>
                      <span className="block truncate text-[11px] font-semibold text-slate-500">
                        {CATEGORY_LABELS[item.category]} · {item.description}
                      </span>
                    </span>
                    <ArrowRight size={14} className="shrink-0 text-slate-400" />
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderTrustStrip = () => (
    <div className="grid grid-cols-2 gap-2 md:flex md:flex-wrap md:justify-center md:gap-3">
      {config.trust.map((item) => (
        <span
          key={item}
          className="inline-flex items-center justify-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-2 text-[9px] font-black uppercase tracking-[0.12em] text-white shadow-lg backdrop-blur-md md:px-4 md:text-[10px]"
        >
          <BadgeCheck size={13} className="text-cyan-200" /> {item}
        </span>
      ))}
    </div>
  );

  const renderAppWebViewProStrip = () => {
    if (!isAppWebViewPro) return null;

    return (
      <div className="mx-auto mt-4 max-w-6xl rounded-[1.6rem] border border-cyan-200/70 bg-white/95 p-4 text-left shadow-[0_18px_50px_rgba(8,47,73,0.18)] backdrop-blur md:hidden">
        <div className="flex items-start gap-3">
          <div className="rounded-2xl bg-cyan-50 p-3 text-cyan-700">
            <Smartphone size={18} />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-[10px] font-black uppercase tracking-[0.16em] text-cyan-700">
              Affichage app optimisé
            </p>
            <h3 className="mt-1 text-base font-black text-slate-950">
              Contrôles mobile renforcés
            </h3>
            <p className="mt-1 text-xs font-semibold leading-relaxed text-slate-600">
              Les modules partenaires sont chargés en mode compatible WebView avec plus d’espace, un bouton de fermeture du calendrier et une barre d’accès rapide en bas de l’écran.
            </p>
          </div>
        </div>
      </div>
    );
  };

  const renderAppWebViewToolbar = () => {
    if (!isAppWebViewPro) return null;

    const scrollToSearch = () => {
      document.getElementById("forfeo-explorer-search")?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    };

    const closeOverlays = () => {
      try {
        (document.activeElement as HTMLElement | null)?.blur?.();
        document.dispatchEvent(new KeyboardEvent("keydown", { key: "Escape", bubbles: true }));
        window.dispatchEvent(new KeyboardEvent("keydown", { key: "Escape", bubbles: true }));
      } catch (error) {
        console.warn("Forfeo app toolbar close failed:", error);
      }
    };

    return (
      <div className="fixed inset-x-0 bottom-0 z-[99980] border-t border-slate-200 bg-white/96 px-3 pb-[calc(env(safe-area-inset-bottom,0px)+0.55rem)] pt-2 shadow-[0_-16px_45px_rgba(15,23,42,0.18)] backdrop-blur md:hidden">
        <div className="mx-auto grid max-w-md grid-cols-4 gap-2">
          <button
            type="button"
            onClick={scrollToSearch}
            className="rounded-2xl bg-slate-100 px-2 py-2.5 text-[9px] font-black uppercase tracking-[0.1em] text-slate-700"
          >
            Recherche
          </button>
          <button
            type="button"
            onClick={() => widgetSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })}
            className="rounded-2xl bg-slate-100 px-2 py-2.5 text-[9px] font-black uppercase tracking-[0.1em] text-slate-700"
          >
            Module
          </button>
          <button
            type="button"
            onClick={closeOverlays}
            className="rounded-2xl bg-slate-950 px-2 py-2.5 text-[9px] font-black uppercase tracking-[0.1em] text-white"
          >
            Fermer
          </button>
          <button
            type="button"
            onClick={() => handleSearch()}
            className="rounded-2xl bg-blue-600 px-2 py-2.5 text-[9px] font-black uppercase tracking-[0.1em] text-white"
          >
            Relancer
          </button>
        </div>
      </div>
    );
  };

  const renderSearchPanel = () => {
    const fieldShell =
      "group rounded-[1.35rem] bg-slate-50 px-4 py-3 transition focus-within:bg-white focus-within:ring-2 focus-within:ring-blue-500/20";
    const labelClass =
      "mb-1 block text-[9px] font-black uppercase tracking-[0.18em] text-slate-400";
    const inputClass =
      "min-h-[36px] w-full bg-transparent text-sm font-black text-slate-950 outline-none placeholder:text-slate-400";

    if (category === "monde") {
      return (
        <form
          onSubmit={handleSearch}
          className="grid gap-2 rounded-[2rem] border border-slate-100 bg-white/95 p-2.5 shadow-2xl ring-1 ring-blue-500/5 backdrop-blur-xl md:grid-cols-[1fr_1fr_0.9fr_0.9fr_0.65fr_auto]"
        >
          <div className={fieldShell}>
            <label className={labelClass}>Départ</label>
            <div className="flex items-center gap-2">
              <Plane size={17} className="text-slate-400" />
              <input
                className={inputClass}
                value={flightOrigin}
                onChange={(e) => setFlightOrigin(e.target.value)}
                placeholder="YUL, YQB, Québec..."
              />
            </div>
          </div>
          <div className={fieldShell}>
            <label className={labelClass}>Arrivée</label>
            <div className="flex items-center gap-2">
              <Globe2 size={17} className="text-slate-400" />
              <input
                className={inputClass}
                value={travelTo}
                onChange={(e) => setTravelTo(e.target.value)}
                placeholder="Paris, Miami, Tokyo..."
              />
            </div>
          </div>
          <div className={fieldShell}>
            <label className={labelClass}>Aller</label>
            <input
              type="date"
              min={todayIso()}
              className={`${inputClass} [color-scheme:light]`}
              value={dates}
              onChange={(e) => setDates(e.target.value)}
            />
          </div>
          <div className={fieldShell}>
            <label className={labelClass}>Retour</label>
            <input
              type="date"
              min={dates || todayIso()}
              className={`${inputClass} [color-scheme:light]`}
              value={checkOutDate}
              onChange={(e) => setCheckOutDate(e.target.value)}
            />
          </div>
          <div className={fieldShell}>
            <label className={labelClass}>Passagers</label>
            <select
              className={`${inputClass} cursor-pointer`}
              value={guests}
              onChange={(e) => setGuests(parseInt(e.target.value, 10))}
            >
              <option value="1">1</option>
              <option value="2">2</option>
              <option value="3">3</option>
              <option value="4">4+</option>
            </select>
          </div>
          <button
            type="submit"
            className="rounded-[1.35rem] bg-blue-600 px-6 py-4 text-[10px] font-black uppercase tracking-[0.16em] text-white shadow-xl shadow-blue-600/20 transition hover:bg-blue-700 md:min-w-[150px]"
          >
            Comparer
          </button>
        </form>
      );
    }

    if (category === "autos") {
      return (
        <form
          onSubmit={handleSearch}
          className="grid gap-2 rounded-[2rem] border border-slate-100 bg-white/95 p-2.5 shadow-2xl ring-1 ring-cyan-500/5 backdrop-blur-xl md:grid-cols-[1fr_1fr_0.85fr_0.85fr_0.7fr_auto]"
        >
          <div className={fieldShell}>
            <label className={labelClass}>Prise en charge</label>
            <div className="flex items-center gap-2">
              <MapPin size={17} className="text-slate-400" />
              <input
                className={inputClass}
                value={pickupLocation}
                onChange={(e) => setPickupLocation(e.target.value)}
                placeholder="Ville, aéroport ou hôtel"
              />
            </div>
          </div>
          <div className={fieldShell}>
            <label className={labelClass}>Retour</label>
            <div className="flex items-center gap-2">
              <ArrowRight size={17} className="text-slate-400" />
              <input
                className={inputClass}
                value={dropoffLocation}
                onChange={(e) => setDropoffLocation(e.target.value)}
                placeholder="Même lieu ou autre"
              />
            </div>
          </div>
          <div className={fieldShell}>
            <label className={labelClass}>Date départ</label>
            <input
              type="date"
              min={todayIso()}
              className={`${inputClass} [color-scheme:light]`}
              value={dates}
              onChange={(e) => setDates(e.target.value)}
            />
          </div>
          <div className={fieldShell}>
            <label className={labelClass}>Date retour</label>
            <input
              type="date"
              min={dates || todayIso()}
              className={`${inputClass} [color-scheme:light]`}
              value={checkOutDate}
              onChange={(e) => setCheckOutDate(e.target.value)}
            />
          </div>
          <div className="grid grid-cols-2 gap-2 md:grid-cols-1">
            <div className={fieldShell}>
              <label className={labelClass}>Heure</label>
              <input
                type="time"
                className={`${inputClass} [color-scheme:light]`}
                value={pickupTime}
                onChange={(e) => setPickupTime(e.target.value)}
              />
            </div>
            <div className={`${fieldShell} md:hidden`}>
              <label className={labelClass}>Retour</label>
              <input
                type="time"
                className={`${inputClass} [color-scheme:light]`}
                value={returnTime}
                onChange={(e) => setReturnTime(e.target.value)}
              />
            </div>
            <input
              type="hidden"
              value={returnTime}
              onChange={(e) => setReturnTime(e.target.value)}
            />
          </div>
          <button
            type="submit"
            className="rounded-[1.35rem] bg-cyan-600 px-6 py-4 text-[10px] font-black uppercase tracking-[0.16em] text-white shadow-xl shadow-cyan-600/20 transition hover:bg-cyan-700 md:min-w-[150px]"
          >
            Rechercher
          </button>
        </form>
      );
    }

    if (category === "experiences") {
      return (
        <form
          onSubmit={handleSearch}
          className="grid gap-2 rounded-[2rem] border border-slate-100 bg-white/95 p-2.5 shadow-2xl ring-1 ring-emerald-500/5 backdrop-blur-xl md:grid-cols-[1.4fr_0.8fr_0.8fr_auto]"
        >
          <div className={fieldShell}>
            <label className={labelClass}>Destination ou activité</label>
            <div className="flex items-center gap-2">
              <Compass size={17} className="text-slate-400" />
              <input
                className={inputClass}
                value={destination}
                onChange={(e) => setDestination(e.target.value)}
                placeholder="Québec, spa, resto, plein air..."
              />
            </div>
          </div>
          <div className={fieldShell}>
            <label className={labelClass}>Date</label>
            <input
              type="date"
              min={todayIso()}
              className={`${inputClass} [color-scheme:light]`}
              value={dates}
              onChange={(e) => setDates(e.target.value)}
            />
          </div>
          <div className={fieldShell}>
            <label className={labelClass}>Type</label>
            <select
              className={`${inputClass} cursor-pointer`}
              value={experienceType}
              onChange={(e) => setExperienceType(e.target.value)}
            >
              <option>Tous</option>
              <option>Détente</option>
              <option>Gastronomie</option>
              <option>Activités</option>
              <option>Séjours</option>
            </select>
          </div>
          <button
            type="submit"
            className="rounded-[1.35rem] bg-emerald-600 px-6 py-4 text-[10px] font-black uppercase tracking-[0.16em] text-white shadow-xl shadow-emerald-600/20 transition hover:bg-emerald-700 md:min-w-[150px]"
          >
            Explorer
          </button>
        </form>
      );
    }

    if (category === "esim") {
      return (
        <form
          onSubmit={handleSearch}
          className="grid gap-2 rounded-[2rem] border border-slate-100 bg-white/95 p-2.5 shadow-2xl ring-1 ring-purple-500/5 backdrop-blur-xl md:grid-cols-[1fr_0.8fr_auto]"
        >
          <div className={fieldShell}>
            <label className={labelClass}>Pays ou région</label>
            <div className="flex items-center gap-2">
              <Smartphone size={17} className="text-slate-400" />
              <input
                className={inputClass}
                value={esimCountry}
                onChange={(e) => setEsimCountry(e.target.value)}
                placeholder="Canada, France, Espagne..."
              />
            </div>
          </div>
          <div className="rounded-[1.35rem] bg-purple-50 px-4 py-3 text-left">
            <label className={labelClass}>Activation</label>
            <p className="min-h-[36px] text-sm font-black text-purple-900">
              QR mobile instantané
            </p>
          </div>
          <button
            type="submit"
            className="rounded-[1.35rem] bg-purple-600 px-6 py-4 text-[10px] font-black uppercase tracking-[0.16em] text-white shadow-xl shadow-purple-600/20 transition hover:bg-purple-700 md:min-w-[150px]"
          >
            Voir eSIM
          </button>
        </form>
      );
    }

    return (
      <form
        onSubmit={handleSearch}
        className="grid gap-2 rounded-[2rem] border border-slate-100 bg-white/95 p-2.5 shadow-2xl ring-1 ring-blue-500/5 backdrop-blur-xl md:grid-cols-[1.45fr_0.8fr_0.8fr_0.55fr_auto]"
      >
        <div className={fieldShell}>
          <label className={labelClass}>Destination</label>
          <div className="flex items-center gap-2">
            <MapPin size={17} className="text-slate-400" />
            <input
              className={inputClass}
              value={destination}
              onChange={(e) => setDestination(e.target.value)}
              placeholder="Où voulez-vous aller ?"
            />
          </div>
        </div>
        <div className={fieldShell}>
          <label className={labelClass}>Arrivée</label>
          <input
            type="date"
            min={todayIso()}
            className={`${inputClass} [color-scheme:light]`}
            value={dates}
            onChange={(e) => setDates(e.target.value)}
          />
        </div>
        <div className={fieldShell}>
          <label className={labelClass}>Départ</label>
          <input
            type="date"
            min={dates || todayIso()}
            className={`${inputClass} [color-scheme:light]`}
            value={checkOutDate}
            onChange={(e) => setCheckOutDate(e.target.value)}
          />
        </div>
        <div className={fieldShell}>
          <label className={labelClass}>Qui ?</label>
          <select
            className={`${inputClass} cursor-pointer`}
            value={guests}
            onChange={(e) => setGuests(parseInt(e.target.value, 10))}
          >
            <option value="1">1</option>
            <option value="2">2</option>
            <option value="3">3</option>
            <option value="4">4+</option>
          </select>
        </div>
        <button
          type="submit"
          className="rounded-[1.35rem] bg-blue-600 px-6 py-4 text-[10px] font-black uppercase tracking-[0.16em] text-white shadow-xl shadow-blue-600/20 transition hover:bg-blue-700 md:min-w-[150px]"
        >
          {loading ? (
            <Loader2 className="mx-auto animate-spin" />
          ) : (
            "Rechercher"
          )}
        </button>
      </form>
    );
  };

  const renderPartnerHero = () => {
    const summaryItems =
      category === "monde"
        ? [
            {
              label: "Départ",
              value: flightOrigin || "À préciser",
              icon: <Plane size={16} />,
            },
            {
              label: "Arrivée",
              value: travelTo || "Destination libre",
              icon: <Globe2 size={16} />,
            },
            {
              label: "Dates",
              value: `${dates || addDaysIso(21)} → ${checkOutDate || addDaysIso(28)}`,
              icon: <Calendar size={16} />,
            },
          ]
        : category === "autos"
          ? [
              {
                label: "Prise",
                value: pickupLocation || "Lieu à choisir",
                icon: <MapPin size={16} />,
              },
              {
                label: "Retour",
                value: dropoffLocation || "Même lieu possible",
                icon: <ArrowRight size={16} />,
              },
              {
                label: "Horaire",
                value: `${pickupTime} → ${returnTime}`,
                icon: <Clock size={16} />,
              },
            ]
          : category === "experiences"
            ? [
                {
                  label: "Recherche",
                  value: destination || "Toutes les idées",
                  icon: <Compass size={16} />,
                },
                {
                  label: "Type",
                  value: experienceType,
                  icon: <Sparkles size={16} />,
                },
                {
                  label: "Date",
                  value: dates || "Flexible",
                  icon: <Calendar size={16} />,
                },
              ]
            : [
                {
                  label: "Pays",
                  value: esimCountry || "Canada",
                  icon: <Globe2 size={16} />,
                },
                {
                  label: "Format",
                  value: "QR eSIM",
                  icon: <Smartphone size={16} />,
                },
                {
                  label: "Activation",
                  value: "Rapide",
                  icon: <CreditCard size={16} />,
                },
              ];

    return (
      <div className="mb-10 grid gap-6 lg:grid-cols-[1.05fr_0.95fr]">
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          className={`relative overflow-hidden rounded-[2.5rem] bg-gradient-to-br ${config.gradient} p-8 text-white shadow-2xl md:p-10`}
        >
          <div className="absolute inset-0 opacity-20">
            <img
              src={config.heroImage}
              alt=""
              className="h-full w-full object-cover"
              loading="lazy"
            />
          </div>
          <div className="absolute inset-0 bg-slate-950/35" />
          <div className="relative z-10">
            <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-2 text-[10px] font-black uppercase tracking-[0.18em] backdrop-blur">
              {config.icon} {config.eyebrow}
            </div>
            <h2 className="max-w-2xl text-4xl font-black leading-[0.95] tracking-tighter md:text-6xl">
              {config.title}{" "}
              <span className="text-cyan-100">{config.highlight}</span>
            </h2>
            <p className="mt-5 max-w-xl text-base font-semibold text-white/82 md:text-lg">
              {config.subtitle}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() =>
                  widgetSectionRef.current?.scrollIntoView({
                    behavior: "smooth",
                    block: "start",
                  })
                }
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-white px-5 py-3 text-[10px] font-black uppercase tracking-[0.16em] text-slate-950 shadow-xl transition hover:-translate-y-0.5 hover:bg-slate-50"
              >
                Ouvrir le comparateur <ArrowRight size={16} />
              </button>
              <button
                type="button"
                onClick={() => navigate("/boutique")}
                className="inline-flex items-center justify-center gap-2 rounded-2xl border border-white/20 bg-white/10 px-5 py-3 text-[10px] font-black uppercase tracking-[0.16em] text-white backdrop-blur transition hover:bg-white/20"
              >
                Voir Forfeo local <ExternalLink size={16} />
              </button>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.08 }}
          className="rounded-[2.5rem] border border-slate-100 bg-white p-6 shadow-xl md:p-8"
        >
          <div className="mb-6 flex items-center justify-between gap-4">
            <div>
              <p className="text-[10px] font-black uppercase tracking-[0.18em] text-slate-400">
                Recherche connectée
              </p>
              <h3 className="text-2xl font-black text-slate-950">
                Votre contexte Forfeo
              </h3>
            </div>
            <div
              className={`${config.iconBox} flex h-13 w-13 items-center justify-center rounded-2xl shadow-lg`}
            >
              {config.icon}
            </div>
          </div>
          <div className="grid gap-3">
            {summaryItems.map((item) => (
              <div
                key={item.label}
                className="flex items-center gap-4 rounded-2xl border border-slate-100 bg-slate-50 p-4"
              >
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white text-blue-600 shadow-sm">
                  {item.icon}
                </div>
                <div className="min-w-0 text-left">
                  <p className="text-[9px] font-black uppercase tracking-[0.16em] text-slate-400">
                    {item.label}
                  </p>
                  <p className="truncate text-sm font-black text-slate-950">
                    {item.value}
                  </p>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-6 rounded-2xl border border-cyan-400/20 bg-slate-950 p-5 text-left text-white">
            <p className="text-[10px] font-black uppercase tracking-[0.18em] text-cyan-300">
              {AFFILIATE_DISCLOSURE_TITLE}
            </p>
            <p className="mt-2 text-xs font-semibold leading-relaxed text-slate-300 md:text-sm">
              {AFFILIATE_DISCLOSURE_TEXT}
            </p>
          </div>
        </motion.div>
      </div>
    );
  };

  const renderLocalExperienceStrip = () => {
    if (category !== "experiences") return null;

    return (
      <div className="mb-10 rounded-[2.5rem] border border-emerald-100 bg-white p-5 shadow-xl md:p-8">
        <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div>
            <p className="text-[10px] font-black uppercase tracking-[0.18em] text-emerald-600">
              Forfeo d’abord
            </p>
            <h3 className="text-2xl font-black tracking-tight text-slate-950 md:text-3xl">
              Expériences locales connectées à la boutique
            </h3>
            <p className="mt-2 max-w-2xl text-sm font-semibold text-slate-500">
              La recherche de cet onglet filtre les forfaits Forfeo disponibles,
              puis complète avec le module mondial plus bas.
            </p>
          </div>
          <button
            type="button"
            onClick={() => navigate("/boutique")}
            className="rounded-2xl bg-slate-950 px-5 py-3 text-[10px] font-black uppercase tracking-[0.16em] text-white shadow-lg transition hover:bg-emerald-700"
          >
            Boutique Forfeo
          </button>
        </div>

        {localExperiencesLoading ? (
          <div className="grid gap-4 md:grid-cols-3">
            {[1, 2, 3].map((item) => (
              <div
                key={item}
                className="h-72 animate-pulse rounded-[2rem] bg-slate-100"
              />
            ))}
          </div>
        ) : filteredLocalExperiences.length === 0 ? (
          <div className="rounded-[2rem] border border-dashed border-slate-200 bg-slate-50 p-8 text-center">
            <p className="font-black text-slate-950">
              Aucune expérience locale ne correspond encore à cette recherche.
            </p>
            <p className="mt-2 text-sm font-semibold text-slate-500">
              Le module mondial ci-dessous peut compléter l’offre.
            </p>
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-3">
            {filteredLocalExperiences.slice(0, 3).map((item: any) => (
              <button
                type="button"
                key={item.id}
                onClick={() => navigate(`/boutique/${item.id}`)}
                className="group overflow-hidden rounded-[2rem] border border-slate-100 bg-white text-left shadow-sm transition hover:-translate-y-1 hover:shadow-2xl"
              >
                <div className="relative h-44 overflow-hidden bg-slate-100">
                  <img
                    src={
                      item.image ||
                      item.image_url ||
                      "https://images.unsplash.com/photo-1500530855697-b586d89ba3ee?q=80&w=2070&auto=format&fit=crop"
                    }
                    alt={item.title || "Expérience Forfeo"}
                    className="h-full w-full object-cover transition duration-700 group-hover:scale-110"
                    loading="lazy"
                  />
                  <div className="absolute left-4 top-4 rounded-full bg-white/95 px-3 py-1.5 text-[9px] font-black uppercase tracking-[0.14em] text-emerald-700 shadow">
                    {item.category || "Forfeo"}
                  </div>
                </div>
                <div className="p-5">
                  <h4 className="line-clamp-2 text-lg font-black leading-tight text-slate-950 group-hover:text-emerald-700">
                    {item.title}
                  </h4>
                  <p className="mt-2 line-clamp-1 text-sm font-semibold text-slate-500">
                    {item.business_name || item.location || "Partenaire Forfeo"}
                  </p>
                  <div className="mt-5 flex items-end justify-between border-t border-slate-100 pt-4">
                    <div>
                      <p className="text-[9px] font-black uppercase tracking-[0.14em] text-slate-400">
                        À partir de
                      </p>
                      <p className="text-2xl font-black text-slate-950">
                        {toNum(item.price, 0)}$
                      </p>
                    </div>
                    <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-950 text-white transition group-hover:bg-emerald-600">
                      <ArrowRight size={17} />
                    </span>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderConversionGovernanceLayer = () => {
    const steps =
      category === "hebergements_locaux"
        ? [
            "Cherchez un séjour local",
            "Comparez la carte et les offres",
            "Réservez avec Forfeo",
          ]
        : [
            "Cherchez dans l’interface Forfeo",
            "Comparez le module partenaire",
            "Validez les conditions finales",
          ];

    const metrics = [
      { label: "Service actif", value: CATEGORY_LABELS[category] },
      { label: "Affiliation", value: category === "hebergements_locaux" ? "Forfeo" : "Déclarée" },
      { label: "Vue", value: "Mobile + ordinateur" },
    ];

    return (
      <section className="mt-10 grid gap-4 md:grid-cols-[1.15fr_0.85fr]">
        <div className="relative overflow-hidden rounded-[2rem] border border-slate-100 bg-white p-5 shadow-sm md:p-7">
          <div className={`absolute -right-14 -top-14 h-40 w-40 rounded-full bg-gradient-to-br ${config.gradient} opacity-15 blur-3xl`} />
          <div className="relative z-10">
            <p className="text-[10px] font-black uppercase tracking-[0.18em] text-slate-400">
              Conversion responsable
            </p>
            <h3 className="mt-2 text-2xl font-black tracking-tight text-slate-950 md:text-3xl">
              Une recherche claire, sans cacher le rôle du partenaire.
            </h3>
            <p className="mt-3 max-w-2xl text-sm font-semibold leading-relaxed text-slate-500">
              Forfeo garde une expérience visuelle premium, mais les modules affiliés restent identifiés pour protéger la confiance des voyageurs et les intérêts de Forfeo au Québec.
            </p>
            <div className="mt-6 grid gap-3 md:grid-cols-3">
              {steps.map((step, index) => (
                <div key={step} className="rounded-2xl border border-slate-100 bg-slate-50 p-4">
                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-950 text-xs font-black text-white">
                    {index + 1}
                  </span>
                  <p className="mt-3 text-sm font-black leading-snug text-slate-900">
                    {step}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="rounded-[2rem] border border-emerald-100 bg-emerald-50/60 p-5 shadow-sm md:p-7">
          <div className="mb-5 flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white text-emerald-700 shadow-sm">
              <ShieldCheck size={20} />
            </span>
            <div>
              <p className="text-[10px] font-black uppercase tracking-[0.18em] text-emerald-700">
                Protection Forfeo
              </p>
              <h3 className="text-xl font-black text-slate-950">
                Cadre de transparence
              </h3>
            </div>
          </div>
          <div className="grid gap-2">
            {metrics.map((metric) => (
              <div key={metric.label} className="flex items-center justify-between rounded-2xl bg-white px-4 py-3">
                <span className="text-[10px] font-black uppercase tracking-[0.14em] text-slate-400">
                  {metric.label}
                </span>
                <span className="text-sm font-black text-slate-950">
                  {metric.value}
                </span>
              </div>
            ))}
          </div>
          <button
            type="button"
            onClick={() => setShowComplianceDetails((value) => !value)}
            className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-slate-950 px-4 py-3 text-[10px] font-black uppercase tracking-[0.15em] text-white"
          >
            {showComplianceDetails ? "Masquer" : "Voir"} la divulgation <ChevronDown size={14} />
          </button>
        </div>

        <AnimatePresence>
          {showComplianceDetails && (
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 12 }}
              className="md:col-span-2"
            >
              <div className="rounded-[2rem] border border-slate-200 bg-white p-5 shadow-sm md:p-7">
                <p className="text-[10px] font-black uppercase tracking-[0.18em] text-slate-400">
                  Note de conformité commerciale
                </p>
                <div className="mt-4 grid gap-4 md:grid-cols-3">
                  {COMPLIANCE_ITEMS.map((item) => (
                    <div key={item.title} className="rounded-2xl bg-slate-50 p-4">
                      <h4 className="text-sm font-black text-slate-950">
                        {item.title}
                      </h4>
                      <p className="mt-2 text-xs font-semibold leading-relaxed text-slate-500">
                        {item.text}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </section>
    );
  };

  const renderPartnerWidget = () => {
    const travelerToolboxCategory = normalizeTravelerToolboxCategory(category);

    if (!travelerToolboxCategory) {
      return null;
    }

    return (
      <TravelerToolbox
        category={travelerToolboxCategory}
      />
    );
  };

  return (
    <div className={`relative min-h-screen bg-slate-50 font-sans ${isAppWebViewPro ? "pb-32" : "pb-12"}`}>
      <Header />

      <Helmet>
        <title>{seo.title}</title>
        <meta name="description" content={seo.description} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={seo.title} />
        <meta property="og:description" content={seo.description} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            name: seo.title,
            description: seo.description,
            url: canonicalUrl,
            isPartOf: {
              "@type": "WebSite",
              name: "Forfeo Explore",
              url: "https://forfeoexplore.com",
            },
          })}
        </script>
      </Helmet>

      <section className="relative overflow-hidden px-4 pb-16 pt-28 text-center md:pb-20 md:pt-32">
        <div className="absolute inset-0 z-0">
          <img
            src={config.heroImage}
            alt="Forfeo Explore"
            className="h-full w-full object-cover brightness-[0.35]"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-950/80 via-slate-950/45 to-slate-50" />
          <div
            className={`absolute inset-x-0 bottom-0 h-56 bg-gradient-to-t ${config.gradient} opacity-20 blur-3xl`}
          />
        </div>

        <div className="relative z-10 mx-auto mt-4 w-full max-w-6xl animate-in slide-in-from-bottom-8 duration-700">
          <span className="mb-5 inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-1.5 text-[10px] font-black uppercase tracking-[0.2em] text-white shadow-xl backdrop-blur-md">
            <Compass size={12} /> Explorer Plus premium
          </span>

          <h1 className="mx-auto mb-6 max-w-5xl text-4xl font-black leading-[0.9] tracking-tighter text-white md:text-7xl">
            {config.title}{" "}
            <span className="relative inline-block text-white drop-shadow-[0_8px_26px_rgba(0,0,0,0.55)] after:absolute after:inset-x-1 after:-bottom-1 after:h-2 after:rounded-full after:bg-white/20">
              {config.highlight}
            </span>
          </h1>
          <p className="mx-auto mb-8 max-w-3xl text-base font-bold leading-relaxed text-white drop-shadow-[0_4px_18px_rgba(0,0,0,0.65)] md:text-lg">
            {config.subtitle}
          </p>

          <div className="relative z-20 mb-8 flex flex-wrap justify-center gap-2 md:gap-3">
            {(Object.values(serviceConfigs) as ServiceConfig[]).map(
              (service) => (
                <button
                  key={service.id}
                  type="button"
                  onClick={() => switchTab(service.id)}
                  className={`flex w-[48%] items-center justify-center gap-2 rounded-xl px-4 py-3 text-center text-[10px] font-black uppercase tracking-[0.15em] transition-all duration-300 sm:w-auto md:rounded-2xl md:px-5 md:py-3.5 md:text-[11px] ${
                    category === service.id
                      ? `${service.iconBox} scale-105 border-transparent shadow-lg`
                      : "border border-white/15 bg-white/10 text-white backdrop-blur-md hover:bg-white/20"
                  }`}
                >
                  {service.icon} <span>{service.tab}</span>
                </button>
              ),
            )}
            <button
              type="button"
              onClick={() => navigate("/last-call")}
              className="flex w-[48%] items-center justify-center gap-2 rounded-xl bg-slate-950 px-4 py-3 text-[10px] font-black uppercase tracking-[0.15em] text-white shadow-xl transition duration-300 hover:-translate-y-1 hover:bg-black sm:w-auto md:rounded-2xl md:px-5 md:py-3.5 md:text-[11px]"
            >
              <Flame className="animate-pulse text-orange-500" size={16} />{" "}
              Flash
            </button>
          </div>

          {renderTrustStrip()}

          <div id="forfeo-explorer-search" className="mx-auto mt-8 max-w-6xl scroll-mt-24">{renderSearchPanel()}</div>
          {renderAppWebViewProStrip()}
          {renderSmartSearchLayer()}
        </div>
      </section>

      <main className="relative mx-auto min-h-[50vh] max-w-7xl px-4 py-10 md:px-6 md:py-12">
        {category === "hebergements_locaux" && (
          <div className="animate-in fade-in duration-500">
            <div className="mb-8 flex flex-col justify-between gap-4 rounded-[2rem] border border-slate-100 bg-white p-5 shadow-sm md:flex-row md:items-end md:p-6">
              <div>
                <span className="mb-2 inline-flex items-center gap-2 rounded-full bg-blue-50 px-3 py-1 text-[10px] font-black uppercase tracking-[0.16em] text-blue-700">
                  <ShieldCheck size={13} /> Inventaire Forfeo
                </span>
                <h2 className="mb-2 text-2xl font-black tracking-tight text-slate-900 md:text-3xl">
                  {loading
                    ? "Recherche en cours..."
                    : `${filteredAccommodations.length} hébergements trouvés`}
                </h2>
                <p className="font-medium text-slate-500">
                  {dates
                    ? `Disponibilités du ${new Date(dates).toLocaleDateString()} au ${
                        checkOutDate
                          ? new Date(checkOutDate).toLocaleDateString()
                          : "..."
                      }`
                    : "Les hébergements les mieux notés cette semaine."}
                </p>
              </div>
              <div className="grid grid-cols-2 gap-2 md:flex md:items-center">
                <button
                  onClick={() => setShowFilters((v) => !v)}
                  className="flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-5 py-3 text-[10px] font-black uppercase tracking-widest text-slate-900 shadow-sm transition hover:bg-slate-50 md:text-xs"
                  type="button"
                >
                  <Filter size={16} /> Filtres
                </button>
                <button
                  onClick={() =>
                    setViewMode((v) => (v === "grid" ? "map" : "grid"))
                  }
                  className="flex items-center justify-center gap-2 rounded-2xl bg-slate-950 px-5 py-3 text-[10px] font-black uppercase tracking-widest text-white shadow-lg transition hover:bg-blue-700 md:text-xs"
                  type="button"
                >
                  {viewMode === "grid" ? (
                    <MapIcon size={16} />
                  ) : (
                    <List size={16} />
                  )}
                  {viewMode === "grid" ? "Carte" : "Liste"}
                </button>
              </div>
            </div>

            <AnimatePresence>
              {showFilters && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="overflow-hidden"
                >
                  <div className="mb-8 rounded-3xl border border-slate-100 bg-white p-6 shadow-sm">
                    <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
                      <div className="flex items-center gap-3 rounded-2xl border border-slate-100 bg-slate-50 px-5 py-3">
                        <input
                          id="onlyMystery"
                          type="checkbox"
                          checked={onlyMystery}
                          onChange={(e) => setOnlyMystery(e.target.checked)}
                          className="h-5 w-5 rounded text-blue-600 focus:ring-blue-500"
                        />
                        <label
                          htmlFor="onlyMystery"
                          className="flex cursor-pointer items-center gap-2 text-sm font-bold text-slate-800"
                        >
                          <Lock size={16} className="text-yellow-500" />{" "}
                          Uniquement Hôtels Mystère
                        </label>
                      </div>
                      <div className="grid w-full grid-cols-2 gap-4 md:w-auto md:grid-cols-3">
                        <div>
                          <label className="mb-1.5 block text-[10px] font-black uppercase tracking-widest text-slate-400">
                            Prix min ($)
                          </label>
                          <input
                            value={priceMin}
                            onChange={(e) => setPriceMin(e.target.value)}
                            type="number"
                            className="w-full rounded-2xl border border-slate-200 bg-slate-50 p-3 text-sm font-bold outline-none transition focus:border-blue-500 md:w-32"
                            placeholder="0"
                          />
                        </div>
                        <div>
                          <label className="mb-1.5 block text-[10px] font-black uppercase tracking-widest text-slate-400">
                            Prix max ($)
                          </label>
                          <input
                            value={priceMax}
                            onChange={(e) => setPriceMax(e.target.value)}
                            type="number"
                            className="w-full rounded-2xl border border-slate-200 bg-slate-50 p-3 text-sm font-bold outline-none transition focus:border-blue-500 md:w-32"
                            placeholder="500"
                          />
                        </div>
                        <div className="col-span-2 md:col-span-1">
                          <label className="mb-1.5 block text-[10px] font-black uppercase tracking-widest text-slate-400">
                            Trier par
                          </label>
                          <select
                            value={sortBy}
                            onChange={(e) =>
                              setSortBy(e.target.value as typeof sortBy)
                            }
                            className="w-full cursor-pointer rounded-2xl border border-slate-200 bg-slate-50 p-3 text-sm font-bold outline-none transition focus:border-blue-500 md:w-48"
                          >
                            <option value="reco">Recommandés</option>
                            <option value="price_asc">Prix (Croissant)</option>
                            <option value="price_desc">
                              Prix (Décroissant)
                            </option>
                            <option value="rating_desc">
                              Note (Décroissant)
                            </option>
                            <option value="discount_desc">
                              Plus grand rabais
                            </option>
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {loading ? (
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
                {[1, 2, 3, 4].map((i) => (
                  <div
                    key={i}
                    className="h-[22rem] animate-pulse rounded-[2rem] bg-slate-200"
                  />
                ))}
              </div>
            ) : viewMode === "grid" ? (
              <div className="mb-16 grid grid-cols-1 gap-6 sm:grid-cols-2 md:mb-20 lg:grid-cols-4">
                {filteredAccommodations.map((item) => (
                  <button
                    type="button"
                    key={item.roomId ? `${item.id}-${item.roomId}` : item.id}
                    onClick={() => navigateToHotel(item)}
                    className={`group relative flex cursor-pointer flex-col overflow-hidden rounded-[2rem] border bg-white text-left shadow-sm transition-all duration-500 hover:-translate-y-2 hover:shadow-2xl ${
                      item.isSecret
                        ? "border-yellow-400/50"
                        : "border-slate-100"
                    }`}
                  >
                    {item.isSecret && (
                      <div className="absolute left-4 top-4 z-20 flex items-center gap-1.5 rounded-lg bg-yellow-400 px-3 py-1.5 text-[9px] font-black uppercase tracking-widest text-slate-900 shadow-lg">
                        <Lock size={12} /> Mystère
                      </div>
                    )}
                    <div className="relative h-56 overflow-hidden bg-slate-100">
                      <img
                        src={item.image}
                        alt={item.name}
                        className={`h-full w-full object-cover transition-transform duration-700 ${
                          item.isSecret
                            ? "scale-110 blur-md grayscale"
                            : "group-hover:scale-110"
                        }`}
                        loading="lazy"
                      />
                      {!item.isSecret && (
                        <div className="absolute right-4 top-4 flex items-center gap-1.5 rounded-xl bg-white/95 px-2.5 py-1.5 text-[11px] font-black shadow-sm backdrop-blur">
                          <Star
                            size={12}
                            className="fill-yellow-500 text-yellow-500"
                          />
                          <span>{Number(item.rating || 0).toFixed(1)}</span>
                        </div>
                      )}
                    </div>
                    <div className="flex flex-1 flex-col p-6">
                      <div className="mb-4">
                        <h3
                          className={`line-clamp-1 text-lg font-black leading-tight transition-colors ${
                            item.isSecret
                              ? "text-slate-800"
                              : "text-slate-900 group-hover:text-blue-600"
                          }`}
                        >
                          {item.name}
                        </h3>
                        {item.businessName ? (
                          <p className="mt-1 text-xs font-bold text-slate-600">
                            {item.businessName}
                          </p>
                        ) : null}
                        <p className="mt-1.5 flex items-center gap-1.5 text-xs font-medium text-slate-500">
                          <MapPin size={12} /> {item.region}, QC
                        </p>
                      </div>
                      <div className="mt-auto flex items-end justify-between border-t border-slate-100 pt-4">
                        <div>
                          {item.originalPrice ? (
                            <span className="mb-0.5 block text-xs font-bold text-slate-400 line-through decoration-red-400">
                              {item.originalPrice}$
                            </span>
                          ) : null}
                          <span className="mb-0.5 block text-[9px] font-black uppercase tracking-widest text-slate-400">
                            À partir de
                          </span>
                          <span className="text-2xl font-black tracking-tight text-slate-900">
                            {toNum(item.price, 0)}${" "}
                            <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
                              /nuit
                            </span>
                          </span>
                        </div>
                        <div
                          className={`flex h-10 w-10 items-center justify-center rounded-xl shadow-md transition-all duration-300 group-hover:scale-110 ${
                            item.isSecret
                              ? "bg-yellow-400 text-slate-900"
                              : "bg-slate-900 text-white group-hover:bg-blue-600"
                          }`}
                        >
                          <ArrowRight size={18} />
                        </div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <div className="mb-16 h-[600px] w-full overflow-hidden rounded-[2.5rem] border border-slate-200 bg-slate-100 shadow-2xl md:mb-20 md:h-[700px]">
                {!isLoaded ? (
                  <div className="flex h-full w-full items-center justify-center">
                    <Loader2 className="animate-spin text-blue-600" size={40} />
                  </div>
                ) : (
                  <GoogleMap
                    mapContainerStyle={mapContainerStyle}
                    center={
                      filteredAccommodations.length > 0
                        ? {
                            lat: Number(filteredAccommodations[0].lat),
                            lng: Number(filteredAccommodations[0].lng),
                          }
                        : centerQuebec
                    }
                    zoom={9}
                    options={{
                      styles: customMapStyles,
                      disableDefaultUI: true,
                      zoomControl: true,
                    }}
                  >
                    {filteredAccommodations.map((item) => (
                      <Marker
                        key={item.id}
                        position={{
                          lat: Number(item.lat),
                          lng: Number(item.lng),
                        }}
                        onClick={() => setSelectedHotel(item)}
                        label={{
                          text: `${toNum(item.price, 0)}$`,
                          color: "white",
                          fontWeight: "900",
                          fontSize: "12px",
                        }}
                        icon={{
                          path: "M-20,0a20,20 0 1,0 40,0a20,20 0 1,0 -40,0",
                          fillColor: item.isSecret ? "#eab308" : "#2563eb",
                          fillOpacity: 1,
                          strokeWeight: 0,
                          scale: 0.8,
                        }}
                      />
                    ))}

                    {selectedHotel && (
                      <InfoWindow
                        position={{
                          lat: Number(selectedHotel.lat),
                          lng: Number(selectedHotel.lng),
                        }}
                        onCloseClick={() => setSelectedHotel(null)}
                      >
                        <button
                          type="button"
                          className="relative w-56 cursor-pointer overflow-hidden rounded-xl bg-white text-left"
                          onClick={() => navigateToHotel(selectedHotel)}
                        >
                          {selectedHotel.isSecret && (
                            <div className="absolute inset-x-0 top-0 z-10 bg-yellow-400 py-1.5 text-center text-[9px] font-black uppercase tracking-widest text-slate-900">
                              Offre Mystère
                            </div>
                          )}
                          <img
                            src={selectedHotel.image}
                            alt={selectedHotel.name}
                            className={`h-32 w-full object-cover ${selectedHotel.isSecret ? "blur-[3px]" : ""}`}
                          />
                          <div className="p-3">
                            <h4 className="mb-1 truncate text-sm font-black leading-tight text-slate-900">
                              {selectedHotel.name}
                            </h4>
                            <p className="truncate text-xs font-medium text-slate-500">
                              <MapPin size={10} className="mr-1 inline" />
                              {selectedHotel.region}
                            </p>
                            <div className="mt-3 flex items-end justify-between">
                              <p className="text-lg font-black leading-none text-slate-900">
                                {selectedHotel.price}${" "}
                                <span className="text-[9px] font-bold uppercase text-slate-400">
                                  /nuit
                                </span>
                              </p>
                              <div className="rounded-lg bg-blue-50 p-1.5 text-blue-600">
                                <ArrowRight size={14} />
                              </div>
                            </div>
                          </div>
                        </button>
                      </InfoWindow>
                    )}
                  </GoogleMap>
                )}
              </div>
            )}
          </div>
        )}

        {category !== "hebergements_locaux" && (
          <div className="animate-in slide-in-from-bottom-4 duration-500">
            {renderPartnerHero()}
            {renderLocalExperienceStrip()}
            <div ref={widgetSectionRef} className="scroll-mt-28">
              <div className="mb-5 flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
                <div>
                  <p className="text-[10px] font-black uppercase tracking-[0.18em] text-slate-400">
                    Service partenaire
                  </p>
                  <h2 className="text-2xl font-black tracking-tight text-slate-950 md:text-4xl">
                    {config.widgetTitle}
                  </h2>
                  <p className="mt-2 max-w-2xl text-sm font-semibold text-slate-500">
                    {config.widgetSubtitle}
                  </p>
                </div>
                <div className="inline-flex items-center gap-2 rounded-full border border-emerald-100 bg-emerald-50 px-4 py-2 text-[10px] font-black uppercase tracking-[0.14em] text-emerald-700">
                  <ShieldCheck size={14} /> Affiliation déclarée
                </div>
              </div>
              <div className={`${isAppWebViewPro ? "mb-3" : "mb-4"} grid gap-3 rounded-[1.6rem] border border-slate-100 bg-white p-4 shadow-sm md:grid-cols-[1fr_auto] md:items-center`}>
                <div>
                  <p className="text-[10px] font-black uppercase tracking-[0.16em] text-slate-400">
                    Parcours utilisateur
                  </p>
                  <p className="mt-1 text-sm font-bold leading-relaxed text-slate-600">
                    {isAppWebViewPro
                      ? "Mode app activé : le module est agrandi, les contrôles restent visibles et le calendrier peut être fermé sans quitter Forfeo."
                      : runtimeContext.is_app_webview
                        ? "Vous êtes dans l’application Forfeo. Le module est optimisé pour rester dans l’app et garder les contrôles de retour visibles."
                        : "Vous pouvez comparer directement dans cette page. Les paramètres saisis plus haut restent associés au service actif."}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => handleSearch()}
                  className="inline-flex items-center justify-center gap-2 rounded-2xl bg-slate-950 px-5 py-3 text-[10px] font-black uppercase tracking-[0.14em] text-white shadow-lg transition hover:bg-blue-700"
                >
                  Actualiser le module <ArrowRight size={14} />
                </button>
              </div>
              {renderPartnerWidget()}
            </div>
            {renderConversionGovernanceLayer()}
          </div>
        )}
      </main>

      <section className="relative overflow-hidden border-t border-slate-100 bg-white px-6 py-24">
        <div className="relative z-10 mx-auto max-w-7xl">
          <div className="mb-16 text-center">
            <span className="mb-3 block text-[10px] font-black uppercase tracking-widest text-blue-600">
              L'écosystème Forfeo
            </span>
            <h2 className="text-3xl font-black tracking-tighter text-slate-900 md:text-5xl">
              Pourquoi rejoindre la communauté ?
            </h2>
          </div>

          <div className="mb-24 grid gap-8 md:grid-cols-2">
            <div className="group relative overflow-hidden rounded-[2.5rem] border border-slate-100 bg-slate-50 p-10 shadow-sm">
              <div className="absolute -right-10 -top-10 h-32 w-32 rounded-full bg-blue-200/50 blur-3xl transition-transform duration-700 group-hover:scale-150" />
              <h3 className="relative z-10 mb-8 flex items-center gap-4 text-2xl font-black text-slate-900 md:text-3xl">
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white shadow-sm">
                  <UserPlus className="text-blue-600" />
                </div>
                Espace Voyageur
              </h3>
              <ul className="relative z-10 mb-10 space-y-6">
                <li className="flex gap-4">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-green-100">
                    <CheckCircle className="text-green-600" size={16} />
                  </div>
                  <div>
                    <h4 className="font-black text-slate-900">
                      Club Fidélité Gratuit
                    </h4>
                    <p className="mt-1 text-sm font-medium text-slate-500">
                      Chaque dollar dépensé vous rapporte des points
                      échangeables.
                    </p>
                  </div>
                </li>
                <li className="flex gap-4">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-green-100">
                    <CheckCircle className="text-green-600" size={16} />
                  </div>
                  <div>
                    <h4 className="font-black text-slate-900">
                      Passeport Numérique
                    </h4>
                    <p className="mt-1 text-sm font-medium text-slate-500">
                      Centralisez vos réservations, billets et itinéraires Forfy
                      au même endroit.
                    </p>
                  </div>
                </li>
              </ul>
              <button
                onClick={() => navigate("/club")}
                className="relative z-10 w-full rounded-2xl bg-blue-600 py-4 text-[10px] font-black uppercase tracking-widest text-white shadow-lg transition hover:bg-blue-700"
                type="button"
              >
                Créer mon compte voyageur
              </button>
            </div>

            <div className="group relative overflow-hidden rounded-[2.5rem] border border-slate-800 bg-slate-900 p-10 text-white shadow-2xl">
              <div className="absolute -bottom-10 -right-10 h-40 w-40 rounded-full bg-orange-500/20 blur-3xl transition-transform duration-700 group-hover:scale-150" />
              <h3 className="relative z-10 mb-8 flex items-center gap-4 text-2xl font-black text-white md:text-3xl">
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/10 shadow-inner">
                  <TrendingUp className="text-orange-400" />
                </div>
                Espace Partenaire
              </h3>
              <ul className="relative z-10 mb-10 space-y-6">
                <li className="flex gap-4">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-blue-500/20">
                    <CheckCircle className="text-blue-400" size={16} />
                  </div>
                  <div>
                    <h4 className="font-black text-white">
                      Revenus directs & Visibilité
                    </h4>
                    <p className="mt-1 text-sm font-medium text-slate-400">
                      Publiez vos hébergements ou expériences et encaissez via
                      Stripe.
                    </p>
                  </div>
                </li>
                <li className="flex gap-4">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-blue-500/20">
                    <CheckCircle className="text-blue-400" size={16} />
                  </div>
                  <div>
                    <h4 className="font-black text-white">
                      Partenaire Relationnel (PRP)
                    </h4>
                    <p className="mt-1 text-sm font-medium text-slate-400">
                      Devenez Directeur Régional et touchez des commissions sur
                      votre région.
                    </p>
                  </div>
                </li>
              </ul>
              <button
                onClick={() => navigate("/register-business")}
                className="relative z-10 w-full rounded-2xl bg-white py-4 text-[10px] font-black uppercase tracking-widest text-slate-900 shadow-xl transition hover:bg-slate-100"
                type="button"
              >
                Inscrire mon entreprise
              </button>
            </div>
          </div>

          <div className="mx-auto max-w-4xl">
            <h2 className="mb-10 flex items-center justify-center gap-3 text-center text-2xl font-black tracking-tight text-slate-900 md:text-3xl">
              <HelpCircle className="text-blue-500" /> Questions Fréquentes
            </h2>
            <div className="grid gap-4">
              {FAQ_ITEMS.map((item, index) => (
                <div
                  key={item.question}
                  className="overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-sm transition-all duration-300"
                >
                  <button
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                    className="flex w-full cursor-pointer items-center justify-between bg-white p-6 text-left transition hover:bg-slate-50"
                    type="button"
                  >
                    <span className="pr-4 text-sm font-black text-slate-900 md:text-base">
                      {item.question}
                    </span>
                    <div
                      className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full transition-colors ${
                        openFaq === index
                          ? "bg-blue-100 text-blue-600"
                          : "bg-slate-100 text-slate-400"
                      }`}
                    >
                      {openFaq === index ? (
                        <ChevronUp size={16} />
                      ) : (
                        <ChevronDown size={16} />
                      )}
                    </div>
                  </button>

                  {openFaq === index && (
                    <div className="animate-in slide-in-from-top-2 border-t border-slate-50 bg-slate-50/50 p-6 pt-0 text-sm font-medium text-slate-600 duration-300 md:text-base">
                      <p className="pt-6 leading-relaxed">{item.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <Footer />
      {renderAppWebViewToolbar()}
    </div>
  );
};

export default ExplorePlus;
